<template>
  <v-container fluid class="flashpay-transfer-page dark-surface">
    <!-- Page Title -->
    <v-layout align-center class="mb-4">
      <v-icon large class="mr-2 white--text">account_balance</v-icon>
      <div class="display-1 font-weight-bold white--text">
        Store Income Dashboard
      </div>
    </v-layout>

    <!-- Toolbar -->
    <v-toolbar app flat color="transparent" class="elevation-0 mb-3">
      <v-btn
        color="primary"
        class="mr-2 gradient-btn"
        @click="startTransferFromSelection"
      >
        <v-icon left>play_circle_filled</v-icon>
        Start Transfer ({{ selectedStores.length }})
      </v-btn>
    </v-toolbar>

    <!-- Store Transfer Item Table  -->
    <v-layout row wrap v-if="isCutoffStoreTransfer">
      <v-flex xs12>
        <v-card class="mb-3 glass-card">
          <v-card-title primary-title class="py-3">
            <v-layout row align-center>
              <div class="headline">Store Transfer Items</div>
              <v-chip
                class="ml-2"
                small
                color="grey darken-3"
                text-color="white"
              >
                {{ Number(storeTransferCount || 0).toLocaleString() }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn
                icon
                :disabled="storeTransferLoading"
                @click="refreshStoreTransferItems()"
              >
                <v-icon color="white">refresh</v-icon>
              </v-btn>
            </v-layout>
          </v-card-title>
          <v-card-text class="pa-3">
            <v-layout row wrap align-center class="mb-2">
              <v-flex xs12 sm6 md3 class="px-1 mb-2">
                <v-text-field
                  dark
                  v-model.trim="storeTransferFilters.batch_id"
                  label="Batch ID"
                  type="number"
                  clearable
                  hide-details
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md3 class="px-1 mb-2">
                <v-text-field
                  dark
                  v-model.trim="storeTransferFilters.merchant_id"
                  label="Merchant ID"
                  type="number"
                  clearable
                  hide-details
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md3 class="px-1 mb-2">
                <v-text-field
                  dark
                  v-model.trim="storeTransferFilters.store_id"
                  label="Store ID"
                  type="number"
                  clearable
                  hide-details
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md3 class="px-1 mb-2">
                <v-select
                  dark
                  :items="storeTransferStatusOptions"
                  v-model="storeTransferFilters.status"
                  label="Item Status"
                  clearable
                  hide-details
                ></v-select>
              </v-flex>

              <v-flex xs12 sm6 md3 class="px-1 mb-2">
                <v-select
                  dark
                  :items="storeTransferRecordStatusOptions"
                  v-model="storeTransferFilters.record_status"
                  label="Batch Record Status"
                  clearable
                  hide-details
                ></v-select>
              </v-flex>
              <v-flex xs12 sm6 md9 class="px-1 mb-2 text-xs-right">
                <v-btn
                  small
                  color="primary"
                  class="mr-2"
                  :disabled="storeTransferLoading"
                  @click="applyStoreTransferFilters"
                >
                  Apply
                </v-btn>
                <v-btn
                  small
                  flat
                  color="white"
                  :disabled="storeTransferLoading"
                  @click="resetStoreTransferFilters"
                >
                  Reset
                </v-btn>
              </v-flex>
            </v-layout>

            <v-data-table
              :headers="storeTransferHeaders"
              :items="storeTransferItems"
              item-key="id"
              class="elevation-0 modern-table"
              :loading="storeTransferLoading"
              :pagination.sync="storeTransferPagination"
              :total-items="storeTransferCount"
              :rows-per-page-items="storeTransferRowsPerPageItems"
            >
              <template slot="progress">
                <v-progress-linear
                  color="primary"
                  indeterminate
                ></v-progress-linear>
              </template>
              <template slot="items" slot-scope="props">
                <td style="min-width: 260px">
                  <div class="font-weight-bold">#{{ props.item.id }}</div>
                  <div class="caption grey--text text--lighten-1">
                    Batch #{{ props.item.batch_id || "-" }}
                    <span class="mx-1">•</span>
                    Merchant #{{ props.item.merchant_id || "-" }}
                    <span class="mx-1">•</span>
                    Store #{{ props.item.store_id || "-" }}
                  </div>
                  <div class="caption grey--text text--lighten-1">
                    Province: {{ storeTransferProvinceName(props.item) }}
                  </div>
                  <div
                    v-if="props.item.external_transfer_id"
                    class="caption grey--text text--lighten-1"
                  >
                    Ext: {{ props.item.external_transfer_id }}
                  </div>
                </td>

                <td class="text-xs-right" style="min-width: 140px">
                  <div class="font-weight-bold">
                    {{ asCurrency(props.item.amount) }}
                  </div>
                  <div class="caption grey--text text--lighten-1">
                    Attempts: {{ Number(props.item.attempt_count || 0) }}
                  </div>
                </td>

                <td style="min-width: 170px">
                  <v-chip
                    small
                    :color="storeTransferStatusColor(props.item.status)"
                    text-color="white"
                    class="ma-0"
                  >
                    {{ props.item.status || "-" }}
                  </v-chip>
                  <div class="caption grey--text text--lighten-1">
                    Batch: {{ storeTransferBatchRecordStatus(props.item) }}
                  </div>
                  <div
                    v-if="props.item.error_code || props.item.error_message"
                    class="caption red--text text--lighten-2"
                  >
                    {{ props.item.error_code || "ERROR" }} -
                    {{ props.item.error_message || "Unknown error" }}
                  </div>
                </td>

                <td style="min-width: 240px">
                  <div class="font-weight-medium">
                    {{ props.item.bank_account_name || "-" }}
                  </div>
                  <div class="caption grey--text text--lighten-1">
                    <v-chip
                      small
                      color="grey darken-3"
                      text-color="white"
                      class="mr-1"
                    >
                      {{ (props.item.bank_code || "-").toUpperCase() }}
                    </v-chip>
                    {{ maskBankAccountNumber(props.item.bank_account_number) }}
                  </div>
                </td>

                <td style="min-width: 190px">
                  <div class="caption grey--text text--lighten-1">
                    Created: {{ formatDateTime(props.item.created_at) }}
                  </div>
                  <div class="caption grey--text text--lighten-1">
                    Transferred: {{ formatDateTime(props.item.transferred_at) }}
                  </div>
                </td>

                <td class="text-xs-center" style="white-space: nowrap">
                  <v-tooltip bottom>
                    <template slot="activator" slot-scope="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        small
                        :disabled="
                          storeTransferLoading ||
                          props.item.status === 'success' ||
                          props.item.status === 'cancelled'
                        "
                        @click.stop="openEditBank(props.item)"
                        round
                      >
                        <v-icon small>edit</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit bank snapshot</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template slot="activator" slot-scope="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        small
                        :disabled="
                          storeTransferLoading ||
                          props.item.status !== 'failed' ||
                          !!storeTransferRetryLoadingById[props.item.id]
                        "
                        :loading="
                          !!storeTransferRetryLoadingById[props.item.id]
                        "
                        @click.stop="onRetry(props.item)"
                      >
                        <v-icon small color="orange">cached</v-icon>
                      </v-btn>
                    </template>
                    <span>Retry transfer</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template slot="activator" slot-scope="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        small
                        :disabled="storeTransferLoading"
                        @click.stop="
                          openTransferItemModal({
                            task: { ...props.item, itemId: props.item.id },
                          })
                        "
                      >
                        <v-icon small color="info">info</v-icon>
                      </v-btn>
                    </template>
                    <span>See detail</span>
                  </v-tooltip>
                </td>
              </template>
              <template slot="no-data">
                <v-alert
                  :value="true"
                  color="grey lighten-3"
                  type="info"
                  class="ma-3"
                  >No transfer items</v-alert
                >
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Top section: Overview + Config -->
    <v-layout row wrap>
      <!-- Profit Overview Table -->
      <v-flex xs12>
        <v-card class="mb-3 glass-card">
          <v-card-title primary-title class="py-3">
            <v-layout row wrap align-center>
              <!-- Title Row -->
              <v-flex xs12 md7 class="mb-3">
                <div class="overview-heading">
                  <div class="overview-kicker">Profit Control Center</div>
                  <div class="headline">Store Profit Overview</div>
                  <div class="overview-subtitle">
                    Track store earnings, review order-level profit rows, and
                    act on cutoff or payout status without leaving this page.
                  </div>
                </div>
              </v-flex>
              <v-flex xs12 md5 class="mb-3">
                <div class="overview-stat-grid">
                  <div class="overview-stat-card">
                    <div class="overview-stat-label">Visible Stores</div>
                    <div class="overview-stat-value">
                      {{ filteredProfits.length }}
                    </div>
                  </div>
                  <div class="overview-stat-card">
                    <div class="overview-stat-label">Visible Net</div>
                    <div class="overview-stat-value">
                      {{ overviewSummary.netTotalDisplay }}
                    </div>
                  </div>
                  <div class="overview-stat-card">
                    <div class="overview-stat-label">Locked Orders</div>
                    <div class="overview-stat-value">
                      {{ overviewSummary.lockedProfitCount }}
                    </div>
                  </div>
                </div>
              </v-flex>

              <!-- Filter Row 1: Date Controls -->
              <v-flex xs12 class="mb-2">
                <v-layout row wrap align-center>
                  <v-flex xs12 sm6 class="px-1">
                    <v-btn-toggle v-model="filters.viewMode" mandatory dense>
                      <v-btn flat value="daily">Daily</v-btn>
                      <v-btn flat value="monthly">Monthly</v-btn>
                    </v-btn-toggle>
                  </v-flex>

                  <v-flex xs12 sm6 class="px-1">
                    <v-menu
                      v-model="menus.date"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                    >
                      <template slot="activator" slot-scope="{ on }">
                        <v-text-field
                          dark
                          v-on="on"
                          :label="
                            filters.viewMode === 'daily' ? 'Date' : 'Month'
                          "
                          readonly
                          hide-details
                          :value="displayDate"
                        ></v-text-field>
                      </template>
                      <v-card>
                        <v-date-picker
                          class="elevation-0"
                          v-model="filters.date"
                          color="primary"
                          :type="
                            filters.viewMode === 'monthly' ? 'month' : 'date'
                          "
                          @input="menus.date = false"
                        ></v-date-picker>
                      </v-card>
                    </v-menu>
                  </v-flex>
                </v-layout>
              </v-flex>

              <!-- Filter Row 2: Location & Search -->
              <v-flex xs12>
                <v-layout row wrap align-center>
                  <v-flex xs12 sm6 class="px-1 mb-2">
                    <v-select
                      dark
                      :items="provinceOptions"
                      v-model="filters.selectedProvince"
                      label="Province"
                      clearable
                      hide-details
                    ></v-select>
                  </v-flex>

                  <v-flex xs12 sm12 md6 lg6 class="px-1 mb-2">
                    <v-text-field
                      dark
                      v-model="filters.search"
                      append-icon="search"
                      label="Search store"
                      single-line
                      hide-details
                      color="primary"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-card-title>

          <v-alert
            v-if="!isLocationSelected"
            :value="true"
            class="ma-3"
            color="blue darken-1"
            outlined
          >
            Please select a province to view data.
          </v-alert>
          <v-alert
            v-else-if="!overviewReady"
            :value="true"
            class="ma-3"
            color="indigo darken-1"
            outlined
          >
            Select a date to fetch Store Profit Overview.
          </v-alert>
          <v-card-text v-else class="pa-3">
            <div class="overview-summary-band">
              <div class="overview-summary-pill">
                <span class="label">Open</span>
                <span class="value">{{ overviewSummary.openStores }}</span>
              </div>
              <div class="overview-summary-pill">
                <span class="label">Partial</span>
                <span class="value">{{ overviewSummary.partialStores }}</span>
              </div>
              <div class="overview-summary-pill">
                <span class="label">Paid</span>
                <span class="value">{{ overviewSummary.paidStores }}</span>
              </div>
              <div class="overview-summary-pill">
                <span class="label">Selection</span>
                <span class="value">{{ selectedStores.length }}</span>
              </div>
            </div>
            <v-data-table
              :headers="profitHeaders"
              :items="filteredProfits"
              item-key="id"
              show-select
              expand
              v-model="selectedStores"
              class="elevation-0 modern-table profit-overview-table"
              :search="filters.search"
              :loading="loadingProfits"
              :pagination.sync="profitPagination"
            >
              <template slot="headerCell" slot-scope="props">
                <v-tooltip bottom>
                  <template slot="activator" slot-scope="{ on }">
                    <span v-on="on" class="table-header-label">
                      {{ props.header.text }}
                    </span>
                  </template>
                  <span>{{ profitHeaderHint(props.header.value) }}</span>
                </v-tooltip>
              </template>
              <template slot="progress">
                <v-progress-linear
                  color="primary"
                  indeterminate
                ></v-progress-linear>
              </template>
              <template slot="items" slot-scope="props">
                <td class="row-hover">
                  <div class="overview-store-cell">
                    <div class="overview-store-avatar">
                      {{ storeInitials(props.item.storeName) }}
                    </div>
                    <div class="row-main">
                      <div class="store-name">{{ props.item.storeName }}</div>
                      <div class="store-sub">Store ID #{{ props.item.id }}</div>
                      <div class="overview-micro-badges">
                        <span class="overview-mini-tag">
                          {{ props.item.profitCount }} orders
                        </span>
                        <span
                          v-if="props.item.hasLockedProfits"
                          class="overview-mini-tag locked"
                        >
                          {{ props.item.lockedProfitCount }} locked
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="text-xs-center">
                  <div class="overview-orders-card">
                    <div class="overview-orders-count">
                      {{ (props.item.orderCodes || []).length }}
                    </div>
                    <v-btn
                      v-if="(props.item.orderCodes || []).length"
                      block
                      small
                      outline
                      color="info"
                      class="overview-link-btn overview-action-btn"
                      @click.stop="openOrderCodesModal(props.item)"
                    >
                      Inspect Details
                    </v-btn>
                    <span v-else class="grey--text text--lighten-1"
                      >No rows</span
                    >
                  </div>
                </td>
                <td class="text-xs-right">
                  <div class="overview-money-stack highlight">
                    <div class="money-label">Net profit</div>
                    <div class="net">
                      {{ props.item.netPriceDisplay }}
                    </div>
                  </div>
                </td>
                <td class="text-xs-center" style="min-width: 180px">
                  <div class="status-card">
                    <v-chip
                      small
                      :color="storeProfitCutoffColor(props.item)"
                      text-color="white"
                      class="mb-1"
                    >
                      {{ storeProfitCutoffLabel(props.item) }}
                    </v-chip>
                    <div class="caption grey--text text--lighten-1">
                      {{ storeProfitCutoffHint(props.item) }}
                    </div>
                  </div>
                </td>
                <td class="text-xs-center" style="min-width: 180px">
                  <div class="status-card">
                    <v-chip
                      small
                      :color="storeProfitPaidColor(props.item)"
                      text-color="white"
                      class="mb-1"
                    >
                      {{ storeProfitPaidLabel(props.item) }}
                    </v-chip>
                    <div class="caption grey--text text--lighten-1">
                      {{ storeProfitPaidHint(props.item) }}
                    </div>
                  </div>
                </td>
                <td class="text-xs-center" style="min-width: 220px">
                  <div class="overview-action-stack">
                    <v-btn
                      v-if="storeProfitPrimaryAction(props.item)"
                      block
                      small
                      class="mb-2"
                      :color="storeProfitPrimaryAction(props.item).color"
                      :outline="!!storeProfitPrimaryAction(props.item).outline"
                      :loading="
                        isStoreProfitActionLoading(
                          props.item,
                          storeProfitPrimaryAction(props.item).key
                        )
                      "
                      :disabled="isStoreProfitActionDisabled(props.item)"
                      @click.stop="runStoreProfitAction(props.item, storeProfitPrimaryAction(props.item))"
                    >
                      {{ storeProfitPrimaryAction(props.item).label }}
                    </v-btn>
                    <v-btn
                      v-if="storeProfitSecondaryAction(props.item)"
                      block
                      small
                      class="mb-2"
                      :color="storeProfitSecondaryAction(props.item).color"
                      :outline="!!storeProfitSecondaryAction(props.item).outline"
                      :loading="
                        isStoreProfitActionLoading(
                          props.item,
                          storeProfitSecondaryAction(props.item).key
                        )
                      "
                      :disabled="isStoreProfitActionDisabled(props.item)"
                      @click.stop="runStoreProfitAction(props.item, storeProfitSecondaryAction(props.item))"
                    >
                      {{ storeProfitSecondaryAction(props.item).label }}
                    </v-btn>
                    <div
                      v-if="props.item.hasLockedProfits || storeProfitStatusGuide(props.item)"
                      class="caption amber--text text--darken-2 overview-action-hint"
                    >
                      {{
                        props.item.hasLockedProfits
                          ? storeProfitLockHint(props.item)
                          : storeProfitStatusGuide(props.item)
                      }}
                    </div>
                  </div>
                </td>
                <td class="text-xs-center" style="width: 72px">
                  <v-btn
                    icon
                    class="table-expand-btn"
                    @click.stop="props.expanded = !props.expanded"
                  >
                    <v-icon>
                      {{
                        props.expanded
                          ? "keyboard_arrow_up"
                          : "keyboard_arrow_down"
                      }}
                    </v-icon>
                  </v-btn>
                </td>
              </template>
              <template slot="expand" slot-scope="props">
                <td
                  :colspan="profitHeaders.length + 1"
                  class="table-expand-cell"
                >
                  <div class="table-expand-panel">
                    <div class="table-expand-grid">
                      <div class="expand-metric-card">
                        <div class="expand-metric-label">Province</div>
                        <div class="expand-metric-value">
                          {{ props.item.provinceName || "—" }}
                        </div>
                      </div>
                      <div class="expand-metric-card">
                        <div class="expand-metric-label">Gross Revenue</div>
                        <div class="expand-metric-value">
                          {{ props.item.totalPriceDisplay }}
                        </div>
                      </div>
                      <div class="expand-metric-card">
                        <div class="expand-metric-label">
                          Average Commission
                        </div>
                        <div class="expand-metric-value badge-gradient">
                          {{ props.item.commissionDisplay }}
                        </div>
                      </div>
                      <div class="expand-metric-card">
                        <div class="expand-metric-label">Status Guard</div>
                        <div class="expand-metric-value">
                          {{
                            props.item.hasLockedProfits
                              ? storeProfitLockHint(props.item)
                              : "Manual actions available"
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </template>
              <template slot="pageText" slot-scope="props">
                <span class="table-page-text">
                  Showing {{ props.pageStart }}-{{ props.pageStop }} of
                  {{ props.itemsLength }} stores
                </span>
              </template>
              <template slot="no-data">
                <v-alert
                  :value="true"
                  color="grey lighten-3"
                  type="info"
                  class="ma-3"
                  >No data</v-alert
                >
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Transfer Configuration Panel -->
      <v-flex xs12 v-if="!isCutoffStoreTransfer">
        <v-card class="glass-card control-panel">
          <v-card-title primary-title class="py-3">
            <div class="headline">Transfer Configuration</div>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <!-- Beautiful loader overlay for Transfer Configuration -->
            <div
              v-if="transferConfigLoading || isHydratingConfig"
              class="config-loader-overlay"
            >
              <div class="loader-content">
                <div class="loader-spinner">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    :size="42"
                    :width="4"
                  ></v-progress-circular>
                </div>
                <div class="loader-text">Loading configuration…</div>
                <div class="loader-bars">
                  <div class="bar shimmer"></div>
                  <div class="bar shimmer"></div>
                  <div class="bar shimmer"></div>
                </div>
              </div>
            </div>
            <v-layout row wrap class="mb-2">
              <v-flex xs6 pr-2>
                <v-select
                  dark
                  :items="provinceOptions"
                  v-model="configProvince"
                  label="Province"
                  hide-details
                ></v-select>
              </v-flex>
              <v-flex xs6 pl-2>
                <v-select
                  dark
                  :items="configDistrictOptions"
                  :disabled="
                    !configProvince ||
                    transferConfigLoading ||
                    isHydratingConfig
                  "
                  v-model="configDistrict"
                  label="District"
                  hide-details
                ></v-select>
              </v-flex>
            </v-layout>
            <v-alert
              v-if="!configDistrict"
              :value="true"
              class="ma-3"
              color="indigo darken-1"
              outlined
            >
              Please select a district to configure automatic record/transfer.
            </v-alert>
            <template v-else>
              <div class="section-frame">
                <div class="section-title">Automatic Record</div>
                <div
                  class="status-led"
                  :class="{ on: config.autoRecord }"
                ></div>
                <v-switch
                  dark
                  class="big-switch"
                  v-model="config.autoRecord"
                  color="primary"
                  label="Enable"
                  :disabled="
                    transferConfigLoading ||
                    isHydratingConfig ||
                    (!config.autoRecord && !recordPrereqsReady)
                  "
                ></v-switch>

                <v-btn-toggle
                  v-model="config.frequency"
                  class="mb-3 seg-control"
                  mandatory
                  :disabled="transferConfigLoading || isHydratingConfig"
                >
                  <v-btn flat value="daily">Daily</v-btn>
                  <v-btn flat value="monthly">Monthly</v-btn>
                </v-btn-toggle>

                <v-layout row wrap>
                  <v-flex xs12 v-if="config.frequency == 'monthly'">
                    <v-select
                      dark
                      :items="scheduleDayOptions"
                      v-model.number="config.scheduleDate"
                      label="Schedule day"
                      hide-details
                      :disabled="transferConfigLoading || isHydratingConfig"
                    ></v-select>
                  </v-flex>
                  <v-flex xs12>
                    <v-menu
                      ref="scheduleTimeMenu"
                      v-model="menus.scheduleTime"
                      :close-on-content-click="false"
                      :return-value.sync="config.scheduleTime"
                      transition="scale-transition"
                      offset-y
                      :disabled="transferConfigLoading || isHydratingConfig"
                    >
                      <template slot="activator" slot-scope="{ on }">
                        <v-text-field
                          dark
                          v-on="on"
                          label="Schedule time"
                          readonly
                          :value="config.scheduleTime"
                          :disabled="transferConfigLoading || isHydratingConfig"
                        ></v-text-field>
                      </template>
                      <v-card>
                        <v-time-picker
                          v-model="tempScheduleTime"
                          format="24hr"
                          color="primary"
                          :disabled="transferConfigLoading || isHydratingConfig"
                        >
                          <v-spacer></v-spacer>
                          <v-btn
                            flat
                            @click="menus.scheduleTime = false"
                            :disabled="
                              transferConfigLoading || isHydratingConfig
                            "
                            >Cancel</v-btn
                          >
                          <v-btn
                            flat
                            color="primary"
                            @click="
                              $refs.scheduleTimeMenu.save(tempScheduleTime)
                            "
                            :disabled="
                              transferConfigLoading || isHydratingConfig
                            "
                            >Save</v-btn
                          >
                        </v-time-picker>
                      </v-card>
                    </v-menu>
                  </v-flex>
                </v-layout>
              </div>

              <div class="section-frame">
                <div class="section-title">Automatic Transfer</div>
                <div
                  class="status-led"
                  :class="{ on: config.autoTransfer }"
                ></div>
                <v-switch
                  dark
                  class="big-switch"
                  v-model="config.autoTransfer"
                  color="primary"
                  label="Enable"
                  :disabled="transferConfigLoading || isHydratingConfig"
                ></v-switch>
              </div>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <!-- Auto-save enabled; manual save button removed -->
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Transfer Records Timeline -->
    <v-card class="glass-card timeline-card" v-if="!isCutoffStoreTransfer">
      <v-card-title primary-title class="py-3">
        <div class="headline d-flex align-center">
          Transfer Records
          <v-divider vertical color="white" class="mr-1 ml-3"></v-divider>
          <v-btn
            icon
            color="white"
            :loading="recordsLoading"
            :disabled="recordsLoading"
            @click="fetchTransferRecords"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text>
        <!-- Redesigned Filter Bar -->
        <v-layout row wrap align-center class="mb-2">
          <v-flex xs12 md4 class="px-1 mb-2 mb-md-0">
            <v-text-field
              dark
              v-model="recordsSearch"
              append-icon="search"
              label="Search (batch id / date)"
              single-line
              hide-details
              clearable
              color="primary"
            ></v-text-field>
          </v-flex>

          <v-flex xs12 md4 class="px-1 mb-2 mb-md-0">
            <v-layout row wrap>
              <v-flex xs6 class="pr-1">
                <v-menu
                  v-model="menus.recordsStart"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template slot="activator" slot-scope="{ on }">
                    <v-text-field
                      dark
                      v-on="on"
                      label="Start"
                      readonly
                      hide-details
                      :value="recordsStartDate"
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-date-picker
                      v-model="recordsStartDate"
                      color="primary"
                      @input="menus.recordsStart = false"
                      class="elevation-0"
                    ></v-date-picker>
                  </v-card>
                </v-menu>
              </v-flex>
              <v-flex xs6 class="pl-1">
                <v-menu
                  v-model="menus.recordsEnd"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template slot="activator" slot-scope="{ on }">
                    <v-text-field
                      dark
                      v-on="on"
                      label="End"
                      readonly
                      hide-details
                      :value="recordsEndDate"
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-date-picker
                      v-model="recordsEndDate"
                      color="primary"
                      @input="menus.recordsEnd = false"
                      class="elevation-0"
                    ></v-date-picker>
                  </v-card>
                </v-menu>
              </v-flex>
            </v-layout>
          </v-flex>

          <v-flex xs12 md4 class="px-1 text-xs-left text-md-right">
            <v-btn-toggle v-model="recordsFilter" dense>
              <v-btn flat value="all">All</v-btn>
              <v-btn flat value="pending">Pending</v-btn>
              <v-btn flat value="in_progress">In Progress</v-btn>
              <v-btn flat value="paused">Paused</v-btn>
              <v-btn flat value="completed">Completed</v-btn>
              <v-btn flat value="cancelled">Cancelled</v-btn>
            </v-btn-toggle>
          </v-flex>
        </v-layout>

        <div class="timeline">
          <div
            v-for="(group, idx) in recordsByDate"
            :key="idx"
            class="timeline-group"
          >
            <div class="timeline-date">{{ group.date }}</div>
            <div class="timeline-items">
              <div
                v-for="rec in group.items"
                :key="rec.id"
                class="timeline-item"
              >
                <div class="line"></div>
                <div class="dot" :class="rec.status"></div>
                <div class="content glass-card">
                  <div class="row header">
                    <div class="title">
                      #{{ rec.id }} • {{ rec.transferDate }}
                      <div class="location-badge">
                        <v-icon small color="primary">location_on</v-icon>
                        <span>{{ getRecordLocation(rec) }}</span>
                      </div>
                    </div>
                    <v-chip
                      small
                      :color="statusColor(rec.status)"
                      text-color="white"
                      >{{ prettyStatus(rec.status) }}</v-chip
                    >
                  </div>
                  <div class="row meta">
                    <div>{{ rec.tasks.length }} stores</div>
                    <div class="amount">{{ asCurrency(rec.totalAmount) }}</div>
                    <div class="flex-grow"></div>
                    <div class="progress">{{ rec.progress }}%</div>
                  </div>
                  <div class="row meta">
                    <div class="caption">
                      Transferred:
                      {{
                        rec.tasks.filter((t) => t.status === "completed").length
                      }}
                      / {{ rec.tasks.length }}
                    </div>
                    <div class="flex-grow"></div>
                    <div
                      class="caption"
                      :class="{ 'green--text': rec.status === 'completed' }"
                    >
                      {{
                        rec.status === "completed"
                          ? "All done"
                          : rec.status === "paused"
                          ? "Paused"
                          : rec.status === "cancelled"
                          ? "Stopped"
                          : "In progress"
                      }}
                    </div>
                  </div>
                  <div class="row meta" v-if="rec.pauseReason">
                    <div class="caption amber--text text--lighten-2">
                      Pause reason: {{ rec.pauseReason }}
                    </div>
                  </div>
                  <div class="store-chip-hint">
                    <v-icon x-small color="grey lighten-1">touch_app</v-icon>
                    <span
                      >Click a store badge to view details and edit bank
                      detail</span
                    >
                  </div>
                  <div class="store-chips">
                    <v-chip
                      v-for="t in rec.tasks.slice(0, 12)"
                      :key="t.storeId"
                      x-small
                      :color="
                        t.status === 'completed'
                          ? 'success'
                          : t.status === 'failed'
                          ? 'error'
                          : t.status === 'in_progress'
                          ? 'info'
                          : 'grey'
                      "
                      text-color="white"
                      class="ma-1 store-chip-with-badge clickable-chip"
                      title="Click to view transfer details"
                      @click="openTransferItemModal({ task: t, record: rec })"
                    >
                      <span class="store-chip-name">{{ t.storeName }}</span>
                      <span class="store-chip-amount">{{
                        asCurrency(t.amount)
                      }}</span>
                    </v-chip>
                    <v-btn
                      v-if="rec.tasks.length > 12"
                      small
                      flat
                      color="primary"
                      @click="openStoreListModal(rec)"
                    >
                      <v-icon small left>list</v-icon>
                      View All ({{ rec.tasks.length }})
                    </v-btn>
                  </div>
                  <div class="actions">
                    <template v-if="false && rec.status === 'pending'">
                      <v-btn
                        small
                        color="primary"
                        @click="startTransfer(rec)"
                        :disabled="!!activeTransfer"
                        ><v-icon left>play_arrow</v-icon>Start</v-btn
                      >
                    </template>
                    <template v-if="rec.status === 'in_progress'">
                      <v-btn
                        small
                        color="warning"
                        @click="pauseTransfer(rec)"
                        :disabled="!!updatingBatchStatus[rec.id]"
                      >
                        <v-icon left>pause</v-icon>Pause</v-btn
                      >
                    </template>
                    <template v-if="rec.status === 'paused'">
                      <v-btn
                        small
                        color="success"
                        @click="resumeTransfer(rec)"
                        :disabled="!!updatingBatchStatus[rec.id]"
                      >
                        <v-icon left>play_arrow</v-icon>Resume</v-btn
                      >
                    </template>

                    <!-- Cancel is allowed for any non-terminal status -->
                    <v-btn
                      v-if="
                        rec.status !== 'completed' && rec.status !== 'cancelled'
                      "
                      small
                      color="error"
                      @click="stopTransfer(rec)"
                      :disabled="!!updatingBatchStatus[rec.id]"
                    >
                      <v-icon left>stop</v-icon>Stop
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Create Transfer Button -->
    <v-btn
      color="primary"
      fixed
      bottom
      right
      large
      @click="openCreateDialog"
      class="create-btn elevation-6 gradient-btn"
      v-if="false"
    >
      <v-icon left>add</v-icon>
      Create Transfer
    </v-btn>

    <!-- Create Transfer Record Modal -->
    <v-dialog
      v-model="createDialog"
      max-width="900px"
      persistent
      transition="dialog-transition"
    >
      <v-card class="glass-card create-modal">
        <v-card-title class="modal-header">
          <div>
            <div class="modal-title gradient-text">Create Transfer Record</div>
            <div class="modal-subtitle">
              {{ getStepDescription() }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon class="close-btn" @click="createDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Step Indicator -->
        <div class="step-indicator">
          <div
            v-for="step in 3"
            :key="step"
            class="step-dot"
            :class="{
              active: currentStep === step,
              completed: currentStep > step,
            }"
            @click="goToStep(step)"
          >
            <span v-if="currentStep > step">✓</span>
            <span v-else>{{ step }}</span>
          </div>
          <div class="step-line"></div>
        </div>

        <v-divider class="modal-divider"></v-divider>
        <v-card-text class="modal-body">
          <v-form ref="createForm" v-model="createFormValid">
            <!-- Step 1: Location & Filters -->
            <v-layout
              row
              wrap
              v-if="currentStep === 1"
              class="step-content step-1"
            >
              <v-flex xs12>
                <div class="step-title">Select Location</div>
                <div class="step-subtitle">
                  Choose province and district for this transfer
                </div>
              </v-flex>
              <v-flex xs12 sm6 class="pr-2">
                <v-select
                  dark
                  :items="provinceOptions"
                  v-model="form.province"
                  label="Province"
                  class="glass-input"
                  :rules="[(v) => !!v || 'Province is required']"
                >
                  <template slot="prepend-inner">
                    <v-icon color="primary">location_on</v-icon>
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-select
                  dark
                  :items="formDistrictOptions"
                  :disabled="!form.province"
                  v-model="form.district"
                  label="District"
                  class="glass-input"
                  :rules="[(v) => !!v || 'District is required']"
                >
                  <template slot="prepend-inner">
                    <v-icon color="primary">map</v-icon>
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  dark
                  type="number"
                  min="0"
                  step="0.01"
                  v-model.number="form.minNetPrice"
                  label="Minimum Net Price (per store)"
                  prefix="฿"
                  class="glass-input"
                  :rules="[(v) => v === '' || v >= 0 || 'Must be >= 0']"
                >
                  <template slot="prepend-inner">
                    <v-icon color="primary">attach_money</v-icon>
                  </template>
                </v-text-field>
                <div class="helper-text">
                  Stores below this amount will not be included
                </div>
              </v-flex>
            </v-layout>

            <!-- Step 2: Store Selection -->
            <v-layout
              row
              wrap
              v-if="currentStep === 2"
              class="step-content step-2"
            >
              <v-flex xs12>
                <div class="step-title">Select Stores</div>
                <div class="summary-card glass-card">
                  <div class="summary-item">
                    <span class="summary-label">Available Stores</span>
                    <span class="summary-value">{{
                      eligibleStores.length
                    }}</span>
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">Total Profit</span>
                    <span class="summary-value">{{
                      asCurrency(totalEligibleProfit)
                    }}</span>
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">Avg Commission</span>
                    <span class="summary-value">{{ avgCommission }}%</span>
                  </div>
                </div>
              </v-flex>
              <v-flex xs12 class="bulk-actions">
                <v-btn small flat @click="selectAllStores">Select All</v-btn>
                <v-btn small flat @click="deselectAllStores"
                  >Deselect All</v-btn
                >
                <v-spacer></v-spacer>
                <span class="selection-count"
                  >{{ form.selectedStoreIds.length }} selected</span
                >
              </v-flex>
              <v-flex xs12>
                <div class="store-grid">
                  <div
                    v-for="store in eligibleStores"
                    :key="store.id"
                    class="store-card"
                    :class="{ selected: isStoreSelected(store.id) }"
                    @click="toggleStoreSelection(store.id)"
                  >
                    <v-checkbox
                      :input-value="isStoreSelected(store.id)"
                      color="primary"
                      hide-details
                      class="store-checkbox"
                      dark
                    ></v-checkbox>
                    <div class="mr-3"></div>

                    <div class="store-info">
                      <div class="store-name">{{ store.storeName }}</div>
                      <div class="store-price">
                        {{ asCurrency(netPrice(store)) }}
                      </div>
                    </div>
                    <v-chip x-small class="commission-badge"
                      >{{ store.commissionRate }}%</v-chip
                    >
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <!-- Step 3: Schedule & Confirmation -->
            <v-layout
              row
              wrap
              v-if="currentStep === 3"
              class="step-content step-3"
            >
              <v-flex xs12>
                <div class="step-title">Schedule & Confirm</div>
              </v-flex>

              <v-flex xs12>
                <div class="confirmation-summary glass-card">
                  <div class="summary-header gradient-text">
                    Transfer Summary
                  </div>
                  <div class="summary-row">
                    <span class="label">Location:</span>
                    <span class="value"
                      >{{ form.province }} > {{ form.district }}</span
                    >
                  </div>
                  <div class="summary-row">
                    <span class="label">Stores Selected:</span>
                    <span class="value">{{
                      form.selectedStoreIds.length
                    }}</span>
                  </div>
                  <div class="summary-row highlight">
                    <span class="label">Total Amount:</span>
                    <span class="value amount">{{
                      asCurrency(selectedStoresTotal)
                    }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">Minimum Threshold:</span>
                    <span class="value">{{
                      asCurrency(form.minNetPrice || 0)
                    }}</span>
                  </div>
                </div>
              </v-flex>

              <v-flex xs12 class="d-flex align-center mb-3">
                <v-switch
                  dark
                  v-model="form.autoCalculate"
                  color="primary"
                  class="auto-calc-switch"
                >
                  <template slot="label">
                    <span class="switch-label"
                      >Auto-calculate amounts (use Net Price)</span
                    >
                  </template>
                </v-switch>
              </v-flex>

              <v-flex xs12>
                <v-card class="glass-card amounts-preview">
                  <v-card-text class="py-3">
                    <v-layout row wrap class="mb-2">
                      <v-flex xs6 sm5 class="preview-label">Store</v-flex>
                      <v-flex xs6 sm7 class="preview-label">Amount</v-flex>
                    </v-layout>
                    <div
                      v-if="form.selectedStoreIds.length === 0"
                      class="caption grey--text"
                    >
                      No stores selected
                    </div>
                    <div v-else>
                      <v-layout
                        row
                        wrap
                        v-for="sid in form.selectedStoreIds"
                        :key="sid"
                        class="py-1"
                      >
                        <v-flex xs6 sm5 class="subtitle-2">{{
                          storeNameById(sid)
                        }}</v-flex>
                        <v-flex xs6 sm7>
                          <v-text-field
                            dark
                            v-model.number="form.amounts[sid]"
                            :disabled="form.autoCalculate"
                            type="number"
                            min="0"
                            step="0.01"
                            prefix="฿"
                            hide-details
                            :rules="[(v) => v >= 0 || 'Must be >= 0']"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>

              <v-flex xs12 sm6>
                <v-menu
                  v-model="menus.formDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template slot="activator" slot-scope="{ on }">
                    <v-text-field
                      v-on="on"
                      label="Transfer date"
                      readonly
                      :rules="[(v) => !!v || 'Required']"
                      :value="
                        form.transferDate ? formatDate(form.transferDate) : ''
                      "
                      dark
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.transferDate"
                    color="primary"
                    @input="menus.formDate = false"
                    class="elevation-0"
                  ></v-date-picker>
                </v-menu>
              </v-flex>

              <v-flex xs12 sm6>
                <v-menu
                  v-model="menus.formTime"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template slot="activator" slot-scope="{ on }">
                    <v-text-field
                      dark
                      v-on="on"
                      label="Transfer time"
                      readonly
                      :rules="[(v) => !!v || 'Required']"
                      :value="form.transferTime"
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-time-picker
                      v-model="form.transferTime"
                      format="24hr"
                      color="primary"
                      @change="menus.formTime = false"
                    ></v-time-picker>
                  </v-card>
                </v-menu>
              </v-flex>

              <v-flex xs12>
                <v-text-field
                  dark
                  v-model="form.notes"
                  label="Notes / Description"
                  counter="200"
                  :rules="[
                    (v) => (v ? v.length <= 200 : true) || 'Max 200 chars',
                  ]"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn flat large class="cancel-btn" @click="createDialog = false">
            <v-icon left>close</v-icon>
            Cancel
          </v-btn>
          <v-spacer></v-spacer>

          <v-btn
            v-if="currentStep > 1"
            flat
            large
            class="cancel-btn"
            @click="previousStep"
          >
            <v-icon left>arrow_back</v-icon>
            Back
          </v-btn>
          <v-btn
            v-if="currentStep < 3"
            large
            class="gradient-btn submit-btn"
            dark
            @click="nextStep"
            :disabled="!canProceedToNextStep"
          >
            Next
            <v-icon right>arrow_forward</v-icon>
          </v-btn>
          <v-btn
            v-else
            large
            class="gradient-btn submit-btn"
            dark
            @click="submitCreateRecord"
            :loading="submitting"
          >
            <v-icon left>check_circle</v-icon>
            Create Transfer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Store List Modal -->
    <v-dialog v-model="storeListModal.show" max-width="1200px" scrollable>
      <v-card class="store-list-modal-card">
        <v-card-title class="py-3">
          <div class="headline">
            Store List - Transfer #{{ storeListModal.record?.id }}
          </div>
          <v-spacer></v-spacer>
          <v-chip
            small
            :color="statusColor(storeListModal.record?.status)"
            text-color="white"
          >
            {{ prettyStatus(storeListModal.record?.status) }}
          </v-chip>
          <v-btn icon @click="storeListModal.show = false" class="ml-2">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 600px">
          <div class="mb-3">
            <v-text-field
              v-model="storeListModal.search"
              append-icon="search"
              label="Search stores"
              single-line
              hide-details
              clearable
              class="store-search-field"
            ></v-text-field>
          </div>
          <v-data-table
            :headers="storeListHeaders"
            :items="filteredStoreList"
            :search="storeListModal.search"
            item-key="storeId"
            class="elevation-0 modern-table"
            :rows-per-page-items="[
              25,
              50,
              100,
              500,
              { text: 'All', value: -1 },
            ]"
          >
            <template slot="items" slot-scope="props">
              <tr>
                <td>
                  <v-icon
                    small
                    :color="
                      props.item.status === 'completed'
                        ? 'success'
                        : props.item.status === 'failed'
                        ? 'error'
                        : props.item.status === 'in_progress'
                        ? 'info'
                        : 'grey'
                    "
                  >
                    {{
                      props.item.status === "completed"
                        ? "check_circle"
                        : props.item.status === "failed"
                        ? "error"
                        : props.item.status === "in_progress"
                        ? "autorenew"
                        : "radio_button_unchecked"
                    }}
                  </v-icon>
                </td>
                <td>{{ props.item.storeId }}</td>
                <td>
                  <div class="store-name">{{ props.item.storeName }}</div>
                </td>
                <td class="text-xs-right">
                  <span class="amount-badge">{{
                    asCurrency(props.item.amount)
                  }}</span>
                </td>
                <td>
                  <v-chip
                    x-small
                    :color="
                      props.item.status === 'completed'
                        ? 'success'
                        : props.item.status === 'failed'
                        ? 'error'
                        : props.item.status === 'in_progress'
                        ? 'info'
                        : 'grey'
                    "
                    text-color="white"
                  >
                    {{
                      props.item.status === "completed"
                        ? "Completed"
                        : props.item.status === "failed"
                        ? "Failed"
                        : props.item.status === "in_progress"
                        ? "In Progress"
                        : "Pending"
                    }}
                  </v-chip>
                </td>

                <td class="text-xs-right">
                  <v-btn
                    small
                    icon
                    color="primary"
                    :disabled="transferItemModal.loading"
                    @click.stop="
                      openTransferItemModal({
                        task: props.item,
                        record: storeListModal.record,
                      })
                    "
                  >
                    <v-icon>info</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
            <template slot="no-data">
              <v-alert :value="true" color="info" class="ma-3">
                No stores found
              </v-alert>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <div class="pa-2">
            <div class="caption">
              <strong>Total Stores:</strong>
              {{ storeListModal.record?.tasks.length || 0 }} |
              <strong>Completed:</strong> {{ completedStoresCount }} |
              <strong>Pending:</strong> {{ pendingStoresCount }} |
              <strong>Total Amount:</strong>
              {{ asCurrency(storeListModal.record?.totalAmount || 0) }}
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Store Transfer Item Detail Modal -->
    <v-dialog v-model="transferItemModal.show" max-width="760px" scrollable>
      <v-card class="store-list-modal-card">
        <v-card-title class="py-3">
          <div class="headline">
            Transfer Item Detail
            <span v-if="transferItemModal.task">
              — {{ transferItemModal.task.storeName }} (ID #{{
                transferItemModal.task.storeId
              }})
            </span>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeTransferItemModal">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh">
          <v-layout
            v-if="transferItemModal.loading"
            justify-center
            class="py-5"
          >
            <v-progress-circular indeterminate color="primary" size="44" />
          </v-layout>

          <v-alert
            v-else-if="transferItemModal.error"
            :value="true"
            type="error"
            class="ma-0"
          >
            {{ transferItemModal.error }}
          </v-alert>

          <div v-else>
            <v-layout row wrap class="mb-2">
              <v-flex xs12 sm6 class="mb-2">
                <div class="caption">
                  <strong>Batch:</strong> #{{
                    transferItemModal.record?.id || "-"
                  }}
                </div>
                <div class="caption">
                  <strong>Batch Status:</strong>
                  {{ prettyStatus(transferItemModal.record?.status || "-") }}
                </div>
                <div
                  class="caption"
                  v-if="transferItemModal.record?.pauseReason"
                >
                  <strong>Pause Reason:</strong>
                  {{ transferItemModal.record.pauseReason }}
                </div>
              </v-flex>
              <v-flex xs12 sm6 class="mb-2">
                <div class="caption">
                  <strong>Store Bank ID:</strong>
                  {{ transferItemModal.task?.storeBankId || "-" }}
                </div>
                <div class="caption">
                  <strong>External Transfer ID:</strong>
                  {{ transferItemModal.task?.externalTransferId || "-" }}
                </div>
              </v-flex>
            </v-layout>

            <v-divider class="my-2"></v-divider>

            <div v-if="transferItemModal.item">
              <v-card flat class="pa-3 mb-3" style="border: 1px solid #e2e8f0">
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="mb-2">
                    <div class="caption">
                      <strong>Item ID:</strong> {{ transferItemModal.item.id }}
                    </div>
                    <div class="caption">
                      <strong>Status:</strong>
                      <v-chip
                        small
                        :color="
                          storeTransferStatusColor(
                            transferItemModal.item.status
                          )
                        "
                        text-color="white"
                      >
                        {{ transferItemModal.item.status || "-" }}
                      </v-chip>
                    </div>
                    <div class="caption">
                      <strong>Amount:</strong>
                      <span class="amount-badge ml-2">{{
                        asCurrency(transferItemModal.item.amount)
                      }}</span>
                    </div>
                    <div class="caption">
                      <strong>Attempt Count:</strong>
                      {{ transferItemModal.item.attempt_count }}
                    </div>
                  </v-flex>

                  <v-flex xs12 sm6 class="mb-2">
                    <div class="caption">
                      <strong>Created At:</strong>
                      {{ transferItemModal.item.created_at || "-" }}
                    </div>
                    <div class="caption">
                      <strong>Transferred At:</strong>
                      {{ transferItemModal.item.transferred_at || "-" }}
                    </div>
                    <div class="caption">
                      <strong>Error Code:</strong>
                      {{ transferItemModal.item.error_code || "-" }}
                    </div>
                    <div class="caption">
                      <strong>Error Message:</strong>
                      {{ transferItemModal.item.error_message || "-" }}
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>

              <v-divider class="my-2"></v-divider>

              <v-card flat class="pa-3 mb-3" style="border: 1px solid #e2e8f0">
                <v-layout align-center>
                  <div class="caption"><strong>Bank Snapshot</strong></div>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="!isCutoffStoreTransfer"
                    small
                    color="primary"
                    class="black--text"
                    @click="openEditBank(transferItemModal.item)"
                    round
                    :disabled="
                      transferItemModal.item.status == 'success' ||
                      transferItemModal.item.status == 'cancelled'
                    "
                  >
                    <v-icon left small>edit</v-icon>
                    Edit
                  </v-btn>
                </v-layout>

                <div
                  v-if="!isCutoffStoreTransfer"
                  class="caption grey--text text--darken-1"
                >
                  Editing updates the StoreTransferItem bank snapshot.
                </div>

                <v-layout row wrap class="mt-2">
                  <v-flex xs12 sm6 class="mb-2">
                    <div class="caption">
                      <strong>Account Name:</strong>
                      {{ transferItemModal.item.bank_account_name || "-" }}
                    </div>
                    <div class="caption">
                      <strong>Account Number:</strong>
                      <span v-if="transferItemModal.item.bank_account_number">
                        {{
                          maskBankAccountNumber(
                            transferItemModal.item.bank_account_number
                          )
                        }}
                        <span class="grey--text text--darken-1">
                          ({{ transferItemModal.item.bank_account_number }})
                        </span>
                      </span>
                      <span v-else>-</span>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-2">
                    <div class="caption">
                      <strong>Bank Code:</strong>
                      {{ transferItemModal.item.bank_code || "-" }}
                    </div>
                    <div class="caption">
                      <strong>External Transfer ID:</strong>
                      {{ transferItemModal.item.external_transfer_id || "-" }}
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>

              <v-divider class="my-2"></v-divider>

              <div class="caption"><h2>Flashpay</h2></div>
              <div v-if="transferItemModal.item.flashpay === null" class="mt-2">
                <v-alert :value="true" type="info" class="ma-0">
                  No external transaction found.
                </v-alert>
              </div>

              <div v-else-if="transferItemModal.item.flashpay" class="mt-2">
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="mb-2">
                    <div class="caption">
                      <strong>OK:</strong>
                      {{
                        transferItemModal.item.flashpay.ok ? "true" : "false"
                      }}
                    </div>
                    <div
                      class="caption"
                      v-if="transferItemModal.item.flashpay.error"
                    >
                      <strong>Error:</strong>
                      {{ transferItemModal.item.flashpay.error }}
                    </div>
                    <div
                      class="caption"
                      v-if="
                        transferItemModal.item.flashpay.raw &&
                        transferItemModal.item.flashpay.raw.code != null
                      "
                    >
                      <strong>Code:</strong>
                      {{ transferItemModal.item.flashpay.raw.code }}
                    </div>
                  </v-flex>

                  <v-flex xs12 sm6 class="mb-2">
                    <div class="caption">
                      <strong>OutTradeNo:</strong>
                      {{
                        transferItemModal.item.flashpay.data
                          ? transferItemModal.item.flashpay.data.outTradeNo ||
                            "-"
                          : "-"
                      }}
                    </div>
                    <div class="caption">
                      <strong>TradeNo:</strong>
                      {{
                        transferItemModal.item.flashpay.data
                          ? transferItemModal.item.flashpay.data.tradeNo || "-"
                          : "-"
                      }}
                    </div>
                    <div class="caption">
                      <strong>Trade Time:</strong>
                      {{
                        transferItemModal.item.flashpay.data
                          ? transferItemModal.item.flashpay.data.tradeTime ||
                            "-"
                          : "-"
                      }}
                    </div>
                    <div class="caption">
                      <strong>Trade Status:</strong>
                      <v-chip
                        v-if="transferItemModal.item.flashpay.data"
                        small
                        :color="
                          flashpayTradeStatusColor(
                            transferItemModal.item.flashpay.data.tradeStatus
                          )
                        "
                        text-color="white"
                      >
                        {{
                          flashpayTradeStatusLabel(
                            transferItemModal.item.flashpay.data.tradeStatus
                          )
                        }}
                      </v-chip>
                      <span v-else class="grey--text text--lighten-1">—</span>
                      <span
                        v-if="
                          transferItemModal.item.flashpay.data &&
                          transferItemModal.item.flashpay.data.tradeStatus !=
                            null
                        "
                        class="grey--text text--lighten-1"
                      >
                        ({{ transferItemModal.item.flashpay.data.tradeStatus }})
                      </span>
                    </div>
                    <div class="caption">
                      <strong>Amount:</strong>
                      {{
                        transferItemModal.item.flashpay.data
                          ? (transferItemModal.item.flashpay.data.cur ||
                              "THB") +
                            " " +
                            String(
                              transferItemModal.item.flashpay.data.paymentAmount
                            )
                          : "-"
                      }}
                    </div>
                    <div class="caption">
                      <strong>Complete Time:</strong>
                      {{
                        transferItemModal.item.flashpay.data
                          ? transferItemModal.item.flashpay.data.completeTime ||
                            "-"
                          : "-"
                      }}
                    </div>
                  </v-flex>
                </v-layout>

                <v-expansion-panel
                  v-if="
                    transferItemModal.item.flashpay.raw &&
                    transferItemModal.item.flashpay.raw.data
                  "
                  class="mt-2"
                >
                  <v-expansion-panel-content>
                    <div slot="header" class="caption">Flashpay Raw Data</div>
                    <pre class="code-block">{{
                      prettyJson(transferItemModal.item.flashpay.raw.data)
                    }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </div>

              <div v-else>
                <v-alert :value="true" type="info" class="ma-0">
                  Flashpay field not found on transfer-item response.
                </v-alert>
              </div>
            </div>

            <div v-else>
              <v-alert :value="true" type="info" class="ma-0">
                No transfer-item details available for this store row.
                <div v-if="transferItemModal.task?.itemId" class="caption">
                  Tried item_id: {{ transferItemModal.task.itemId }}
                </div>
              </v-alert>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="closeTransferItemModal"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Edit bank dialog for Store Transfer Item -->
    <v-dialog v-model="storeTransferDialog.show" max-width="560" persistent>
      <v-card>
        <v-card-title> <h2>Edit Store Bank</h2> </v-card-title>
        <v-card-text>
          <v-container fluid>
            <v-layout row wrap>
              <v-flex xs12>
                <v-text-field
                  v-model="editBankForm.bank_account_name"
                  label="Account Name"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="editBankForm.bank_account_number"
                  label="Account Number"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="editBankForm.bank_code"
                  label="Bank Code"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="storeTransferDialog.show = false">Cancel</v-btn>
          <v-btn color="primary" flat @click="submitEditBank">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
    >
      {{ snackbar.message }}
      <v-btn flat @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>

    <!-- Order code list modal (Store Profit Overview) -->
    <v-dialog v-model="orderCodesModal.show" max-width="1180">
      <v-card class="order-profit-dialog">
        <v-card-title class="order-profit-hero">
          <div class="order-profit-hero-copy">
            <div class="order-profit-kicker">Order Drilldown</div>
            <div class="headline">Order Profit Details</div>
            <div class="order-profit-subtitle">
              Review row-by-row cutoff and payout state before making partial
              changes.
            </div>
            <div class="order-profit-store" v-if="orderCodesModal.storeName">
              {{ orderCodesModal.storeName }}
              <span>ID #{{ orderCodesModal.storeId }}</span>
            </div>
          </div>
          <div class="order-profit-hero-actions">
            <div class="order-profit-stat-pill">
              <span class="label">Visible</span>
              <span class="value">{{ filteredOrderModalRows.length }}</span>
            </div>
            <div class="order-profit-stat-pill">
              <span class="label">Selected</span>
              <span class="value">{{
                orderCodesModal.selectedProfitIds.length
              }}</span>
            </div>
            <div
              class="order-profit-stat-pill danger"
              v-if="lockedOrderRowsCount > 0"
            >
              <span class="label">Locked</span>
              <span class="value">{{ lockedOrderRowsCount }}</span>
            </div>
            <v-btn
              icon
              class="order-profit-close"
              @click="orderCodesModal.show = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text class="order-profit-body">
          <div class="order-profit-summary">
            <div class="order-profit-summary-card">
              <div class="summary-label">Visible Net Profit</div>
              <div class="summary-value">
                {{ orderModalSummary.netTotalDisplay }}
              </div>
            </div>
            <div class="order-profit-summary-card">
              <div class="summary-label">Cutoff Rows</div>
              <div class="summary-value">
                {{ orderModalSummary.cutoffCount }}
              </div>
            </div>
            <div class="order-profit-summary-card">
              <div class="summary-label">Paid Rows</div>
              <div class="summary-value">{{ orderModalSummary.paidCount }}</div>
            </div>
            <div class="order-profit-summary-card">
              <div class="summary-label">Editable Rows</div>
              <div class="summary-value">{{ selectableOrderRows.length }}</div>
            </div>
          </div>

          <div class="order-profit-toolbar">
            <div class="order-profit-filters">
              <v-text-field
                v-model.trim="orderCodesModal.search"
                append-icon="search"
                label="Search order code"
                single-line
                hide-details
                clearable
                class="order-profit-search"
              ></v-text-field>
              <v-select
                :items="orderModalStatusOptions"
                v-model="orderCodesModal.statusFilter"
                label="Status Filter"
                hide-details
                class="order-profit-filter"
              ></v-select>
            </div>

            <div class="order-profit-quick-actions">
              <v-btn
                small
                flat
                color="primary"
                class="mr-2"
                :disabled="selectableOrderRows.length === 0"
                @click="selectAllOrderModalRows"
              >
                Select Editable
              </v-btn>
              <v-btn
                small
                flat
                color="white"
                :disabled="orderCodesModal.selectedProfitIds.length === 0"
                @click="clearSelectedOrderModalRows"
              >
                Clear
              </v-btn>
            </div>
          </div>

          <div class="order-profit-bulk-grid">
            <v-btn
              v-if="selectedOrderBulkPrimaryAction"
              small
              :color="selectedOrderBulkPrimaryAction.color"
              :outline="!!selectedOrderBulkPrimaryAction.outline"
              :loading="
                orderCodesModal.bulkLoading[selectedOrderBulkPrimaryAction.key]
              "
              :disabled="
                orderCodesModal.selectedProfitIds.length === 0 ||
                !selectedOrderBulkActionReady ||
                orderCodesModal.bulkLoading.cutoff ||
                orderCodesModal.bulkLoading.paid
              "
              @click="runBulkOrderProfitAction(selectedOrderBulkPrimaryAction)"
            >
              {{ selectedOrderBulkPrimaryAction.label }}
            </v-btn>
            <v-btn
              v-if="selectedOrderBulkSecondaryAction"
              small
              :color="selectedOrderBulkSecondaryAction.color"
              :outline="!!selectedOrderBulkSecondaryAction.outline"
              :loading="
                orderCodesModal.bulkLoading[selectedOrderBulkSecondaryAction.key]
              "
              :disabled="
                orderCodesModal.selectedProfitIds.length === 0 ||
                !selectedOrderBulkActionReady ||
                orderCodesModal.bulkLoading.cutoff ||
                orderCodesModal.bulkLoading.paid ||
                orderCodesModal.bulkLoading[
                  selectedOrderBulkSecondaryAction.key
                ]
              "
              @click="runBulkOrderProfitAction(selectedOrderBulkSecondaryAction)"
            >
              {{ selectedOrderBulkSecondaryAction.label }}
            </v-btn>
          </div>

          <v-alert
            v-if="selectedOrderSelectionHint"
            :value="true"
            type="info"
            outlined
            class="mb-3"
          >
            {{ selectedOrderSelectionHint }}
          </v-alert>

          <v-alert
            v-if="lockedOrderRowsCount > 0"
            :value="true"
            type="warning"
            outlined
            class="mb-3 order-profit-warning"
          >
            {{ lockedOrderRowsCount }} order(s) are locked because they were
            already attached to a transfer batch. Editing them here may make
            Store Profit status inconsistent with payout batch processing.
          </v-alert>

          <v-alert
            v-if="orderCodesModal.rows.length === 0"
            :value="true"
            type="info"
            color="grey lighten-3"
            class="ma-0"
          >
            No order profit rows
          </v-alert>
          <v-data-table
            v-else
            :headers="orderModalHeaders"
            :items="filteredOrderModalRows"
            item-key="profitId"
            expand
            hide-actions
            class="elevation-0 modern-table order-profit-table"
          >
            <template slot="headerCell" slot-scope="props">
              <v-tooltip bottom>
                <template slot="activator" slot-scope="{ on }">
                  <span v-on="on" class="table-header-label">
                    {{ props.header.text }}
                  </span>
                </template>
                <span>{{ orderHeaderHint(props.header.value) }}</span>
              </v-tooltip>
            </template>
            <template slot="items" slot-scope="props">
              <td class="text-xs-center" style="width: 56px">
                <v-checkbox
                  :input-value="isOrderModalSelected(props.item.profitId)"
                  :disabled="!props.item.isEditable"
                  hide-details
                  color="primary"
                  @change="toggleOrderModalSelection(props.item)"
                ></v-checkbox>
              </td>
              <td style="min-width: 170px">
                <div class="order-profit-row-title">
                  <div class="order-profit-code">
                    {{ props.item.orderCode || "-" }}
                  </div>
                  <div class="caption grey--text text--lighten-1">
                    Profit #{{ props.item.profitId }}
                  </div>
                </div>
              </td>
              <td style="min-width: 140px">
                <div class="order-profit-time-card">
                  <div class="time-label">Order time</div>
                  <div class="time-value">
                    {{ formatDateTime(props.item.orderDateTime) }}
                  </div>
                </div>
              </td>
              <td class="text-xs-right" style="min-width: 120px">
                <div class="overview-money-stack compact highlight">
                  <div class="money-label">Net</div>
                  <div class="money-value net">
                    {{ asCurrency(props.item.netPrice) }}
                  </div>
                </div>
              </td>
              <td class="text-xs-center" style="min-width: 130px">
                <div class="status-card compact">
                  <v-chip
                    small
                    :color="props.item.isCutoff ? 'info' : 'grey'"
                    text-color="white"
                  >
                    {{ props.item.isCutoff ? "Cutoff" : "Open" }}
                  </v-chip>
                  <div class="caption grey--text text--lighten-1">
                    {{
                      props.item.cutoffDate
                        ? moment(props.item.cutoffDate).format("YYYY-MM-DD")
                        : "-"
                    }}
                  </div>
                </div>
              </td>
              <td class="text-xs-center" style="min-width: 130px">
                <div class="status-card compact">
                  <v-chip
                    small
                    :color="props.item.isPaid ? 'success' : 'grey'"
                    text-color="white"
                  >
                    {{ props.item.isPaid ? "Paid" : "Unpaid" }}
                  </v-chip>
                  <div class="caption grey--text text--lighten-1">
                    {{
                      props.item.paidAt
                        ? formatDateTime(props.item.paidAt)
                        : "-"
                    }}
                  </div>
                </div>
              </td>
              <td style="min-width: 160px">
                <div
                  class="order-lock-card"
                  :class="{ locked: props.item.storeTransferItemId }"
                >
                  <div class="lock-title">
                    {{
                      props.item.storeTransferItemId
                        ? `Locked in batch #${props.item.storeTransferItemId}`
                        : "Editable"
                    }}
                  </div>
                  <div class="lock-copy">
                    {{
                      props.item.storeTransferItemId
                        ? "Protected from manual edits while batch payout state exists."
                        : "Follow new -> cutoff -> paid, and undo one step at a time."
                    }}
                  </div>
                </div>
              </td>
              <td class="text-xs-center" style="min-width: 190px">
                <div class="order-profit-row-actions">
                  <v-btn
                    v-if="orderProfitPrimaryAction(props.item)"
                    small
                    :color="orderProfitPrimaryAction(props.item).color"
                    :outline="!!orderProfitPrimaryAction(props.item).outline"
                    class="mr-2"
                    :loading="
                      isOrderModalRowLoading(
                        props.item.profitId,
                        orderProfitPrimaryAction(props.item).key
                      )
                    "
                    :disabled="
                      !props.item.isEditable ||
                      isOrderModalRowBusy(props.item.profitId)
                    "
                    @click.stop="runOrderProfitAction(props.item, orderProfitPrimaryAction(props.item))"
                  >
                    {{ orderProfitPrimaryAction(props.item).label }}
                  </v-btn>
                  <v-btn
                    v-if="orderProfitSecondaryAction(props.item)"
                    small
                    :color="orderProfitSecondaryAction(props.item).color"
                    :outline="!!orderProfitSecondaryAction(props.item).outline"
                    :loading="
                      isOrderModalRowLoading(
                        props.item.profitId,
                        orderProfitSecondaryAction(props.item).key
                      )
                    "
                    :disabled="
                      !props.item.isEditable ||
                      isOrderModalRowBusy(props.item.profitId)
                    "
                    @click.stop="runOrderProfitAction(props.item, orderProfitSecondaryAction(props.item))"
                  >
                    {{ orderProfitSecondaryAction(props.item).label }}
                  </v-btn>
                </div>
              </td>
              <td class="text-xs-center" style="width: 72px">
                <v-btn
                  icon
                  class="table-expand-btn"
                  @click.stop="props.expanded = !props.expanded"
                >
                  <v-icon>
                    {{
                      props.expanded
                        ? "keyboard_arrow_up"
                        : "keyboard_arrow_down"
                    }}
                  </v-icon>
                </v-btn>
              </td>
            </template>
            <template slot="expand" slot-scope="props">
              <td :colspan="orderModalHeaders.length" class="table-expand-cell">
                <div class="table-expand-panel">
                  <div class="table-expand-grid order-expand-grid">
                    <div class="expand-metric-card">
                      <div class="expand-metric-label">Gross Amount</div>
                      <div class="expand-metric-value">
                        {{ asCurrency(props.item.totalPrice) }}
                      </div>
                    </div>
                    <div class="expand-metric-card">
                      <div class="expand-metric-label">Commission</div>
                      <div class="expand-metric-value">
                        {{ props.item.commissionDisplay }}
                      </div>
                    </div>
                    <div class="expand-metric-card">
                      <div class="expand-metric-label">Cutoff Date</div>
                      <div class="expand-metric-value">
                        {{
                          props.item.cutoffDate
                            ? moment(props.item.cutoffDate).format("YYYY-MM-DD")
                            : "Not cutoff"
                        }}
                      </div>
                    </div>
                    <div class="expand-metric-card">
                      <div class="expand-metric-label">Paid At</div>
                      <div class="expand-metric-value">
                        {{
                          props.item.paidAt
                            ? formatDateTime(props.item.paidAt)
                            : "Not paid"
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="order-profit-footer">
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="orderCodesModal.show = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapActions, mapState } from "vuex";

export default {
  name: "FlashpayTransferMoney",
  data() {
    return {
      isCutoffStoreTransfer: false,
      storeTransferHeaders: [
        { text: "Item", value: "id", sortable: false },
        { text: "Amount", value: "amount", align: "right", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Bank Snapshot", value: "bank", sortable: false },
        { text: "Time", value: "time", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      storeTransferRetryLoadingById: {},
      storeTransferPagination: {
        page: 1,
        rowsPerPage: 20,
      },
      storeTransferRowsPerPageItems: [10, 20, 50, 100, 200],
      storeTransferPaginationReady: false,
      storeTransferFilters: {
        batch_id: "",
        merchant_id: "",
        store_id: "",
        status: null,
        record_status: null,
      },
      storeTransferStatusOptions: [
        { text: "pending", value: "pending" },
        { text: "in_progress", value: "in_progress" },
        { text: "success", value: "success" },
        { text: "failed", value: "failed" },
        { text: "cancelled", value: "cancelled" },
      ],
      storeTransferRecordStatusOptions: [
        { text: "pending", value: "pending" },
        { text: "in_progress", value: "in_progress" },
        { text: "paused", value: "paused" },
        { text: "completed", value: "completed" },
        { text: "cancelled", value: "cancelled" },
      ],
      // Overview
      profitHeaders: [
        { text: "Store", value: "storeName", sortable: true },
        { text: "Orders", value: "orderCodes", sortable: false, width: 140 },
        {
          text: "Net",
          value: "netPriceDisplay",
          sortable: false,
          align: "right",
        },
        {
          text: "Cutoff",
          value: "cutoffSummary",
          sortable: false,
          align: "center",
        },
        {
          text: "Payout",
          value: "paidSummary",
          sortable: false,
          align: "center",
        },
        {
          text: "Actions",
          value: "profitActions",
          sortable: false,
          align: "center",
        },
        {
          text: "Details",
          value: "expand",
          sortable: false,
          align: "center",
          width: 72,
        },
      ],
      profits: [],
      incomeProfits: [],
      profitPagination: { rowsPerPage: 5 },
      selectedStores: [],
      storeProfitActionLoadingByKey: {},
      filters: {
        viewMode: "daily",
        date: null,
        search: "",
        selectedProvince: null,
      },
      menus: {
        date: false,
        scheduleDate: false,
        scheduleTime: false,
        formDate: false,
        formTime: false,
        recordsStart: false,
        recordsEnd: false,
      },
      tempScheduleTime: "09:00",
      // Dashboard/visuals
      loadingProfits: true,
      stats: {
        totalProfitDisplay: "฿0.00",
        pendingAmountDisplay: "฿0.00",
        completedTodayDisplay: "฿0.00",
      },
      trendOptions: {
        chart: {
          sparkline: { enabled: false },
          toolbar: { show: false },
          foreColor: "#CBD5E1",
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6,
            columnWidth: "55%",
            dataLabels: { position: "top" },
          },
        },
        stroke: { show: false },
        fill: { opacity: 0.9 },
        grid: { borderColor: "rgba(255,255,255,0.06)" },
        xaxis: { type: "datetime", labels: { datetimeUTC: false } },
        yaxis: {
          labels: { formatter: (v) => `฿${Number(v).toLocaleString()}` },
        },
        colors: ["#7C3AED"],
        tooltip: { x: { show: true } },
      },
      trendSeries: [],
      sparkOptions: {
        chart: { sparkline: { enabled: true } },
        stroke: { curve: "smooth", width: 2 },
        tooltip: { enabled: false },
        colors: ["#22D3EE"],
      },

      // Config
      config: {
        autoRecord: false,
        autoTransfer: false,
        scheduleDate: moment().format("YYYY-MM-DD"),
        scheduleTime: "09:00",
        frequency: "daily",
      },
      // Location-aware config selection and persistence
      configProvince: null,
      configDistrict: null,
      configMap: {},
      // District lists fetched from store per current selections
      configDistrictOptionsList: [],
      // Debounce timer for auto-saving config
      saveTimer: null,
      // Suppress auto-save while hydrating config from GET
      isHydratingConfig: false,
      // Timestamp barrier to suppress auto-save shortly after hydration
      hydratingUntil: 0,
      // Prevent recursive watcher when reverting invalid autoRecord toggle
      guardingAutoRecordToggle: false,
      // Last server config for current district (API field names)
      lastServerConfig: null,

      // Transfers
      transferRecords: [],
      activeTransferId: null,
      progressTimer: null,
      autoRunnerTimer: null,
      lastAutoRunMap: {},
      progressVisibleLimit: 30,

      // Records table
      recordsSearch: "",
      recordsFilter: "all",
      recordsStartDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
      recordsEndDate: moment().format("YYYY-MM-DD"),
      recordsLoading: false,
      recordHeaders: [
        { text: "ID", value: "id", sortable: true, width: 70 },
        { text: "Date & Time", value: "transferDate", sortable: true },
        { text: "Stores", value: "storesCount", sortable: false, width: 90 },
        {
          text: "Total Amount",
          value: "totalAmount",
          sortable: true,
          align: "right",
        },
        { text: "Status", value: "status", sortable: false, width: 140 },
        { text: "Progress", value: "progress", sortable: true, width: 110 },
        { text: "Actions", value: "actions", sortable: false, width: 160 },
      ],

      // Create dialog
      createDialog: false,
      createFormValid: false,
      currentStep: 1,
      submitting: false,
      form: {
        province: null,
        district: null,
        minNetPrice: 0,
        selectedStoreIds: [],
        autoCalculate: true,
        amounts: {},
        transferDate: moment().format("YYYY-MM-DD"),
        transferTime: moment().format("HH:mm"),
        notes: "",
      },

      // UI
      snackbar: { show: false, color: "success", message: "", timeout: 3000 },
      recordExpanded: {},

      // Transfer batch status update guard (by batch id)
      updatingBatchStatus: {},

      // Store List Modal
      storeListModal: {
        show: false,
        record: null,
        search: "",
      },

      // Transfer item detail modal
      transferItemModal: {
        show: false,
        loading: false,
        error: null,
        item: null,
        task: null,
        record: null,
      },

      // Order code list modal (for Store Profit Overview)
      orderCodesModal: {
        show: false,
        storeId: null,
        storeName: "",
        orderCodes: [],
        rows: [],
        search: "",
        statusFilter: "all",
        selectedProfitIds: [],
        bulkLoading: {
          cutoff: false,
          paid: false,
          cutoffUndo: false,
          paidUndo: false,
        },
        rowLoadingByKey: {},
      },
      orderModalHeaders: [
        { text: "", value: "selected", sortable: false, width: 56 },
        { text: "Order", value: "orderCode", sortable: false },
        { text: "Order Time", value: "orderDateTime", sortable: false },
        { text: "Net", value: "netPrice", sortable: false, align: "right" },
        { text: "Cutoff", value: "isCutoff", sortable: false, align: "center" },
        { text: "Payout", value: "isPaid", sortable: false, align: "center" },
        { text: "Guard", value: "guard", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
        {
          text: "Details",
          value: "expand",
          sortable: false,
          align: "center",
          width: 72,
        },
      ],
      orderModalStatusOptions: [
        { text: "All", value: "all" },
        { text: "Open", value: "open" },
        { text: "Cutoff only", value: "cutoff_only" },
        { text: "Paid", value: "paid" },
        { text: "Locked", value: "locked" },
      ],
      // Store Transfer UI
      storeTransferDialog: {
        show: false,
      },
      editBankForm: {
        item_id: null,
        bank_account_number: "",
        bank_account_name: "",
        bank_code: "",
        store_bank_id: null,
        store_bank_bank_id: null,
      },
      storeListHeaders: [
        { text: "", value: "icon", sortable: false, width: 50 },
        { text: "Store ID", value: "storeId", sortable: true, width: 100 },
        { text: "Store Name", value: "storeName", sortable: true },
        { text: "Amount", value: "amount", sortable: true, align: "right" },
        { text: "Status", value: "status", sortable: true, width: 120 },
        { text: "", value: "actions", sortable: false, width: 60 },
      ],

      // Locations sourced from Vuex store (fetchProvinceBack)
    };
  },
  computed: {
    ...mapState({
      provinceData: (state) => state.fetchProvinceBack.provinceData,
      transferConfigLoading: (state) => state.StoreTransferConfig.loading,
      storeTransferItems: (state) => state.StoreTransferItem.list,
      storeTransferLoading: (state) => state.StoreTransferItem.loading,
      storeTransferCount: (state) => state.StoreTransferItem.count,
    }),
    // Store list modal computed properties
    filteredStoreList() {
      if (!this.storeListModal.record) return [];
      return this.storeListModal.record.tasks || [];
    },
    completedStoresCount() {
      if (!this.storeListModal.record) return 0;
      return (this.storeListModal.record.tasks || []).filter(
        (t) => t.status === "completed"
      ).length;
    },
    pendingStoresCount() {
      if (!this.storeListModal.record) return 0;
      return (this.storeListModal.record.tasks || []).filter(
        (t) => t.status !== "completed"
      ).length;
    },
    // Auto Record prerequisites readiness (schedule time and, for monthly, day)
    recordPrereqsReady() {
      return this.validateRecordPrereqs();
    },
    scheduleDayOptions() {
      return Array.from({ length: 31 }, (_, i) => i + 1);
    },
    displayDate() {
      if (!this.filters.date) return "";
      if (this.filters.viewMode === "daily")
        return this.formatDate(this.filters.date);
      return moment(this.filters.date).format("MMMM YYYY");
    },
    overviewReady() {
      return !!this.filters.date;
    },
    filteredProfits() {
      if (!this.filters.date) return [];
      // Group by store within selected range and aggregate totals
      const d = moment(this.filters.date);
      const inRange = this.profits.filter((p) => {
        const pd = moment(p.date);
        return this.filters.viewMode === "daily"
          ? pd.isSame(d, "day")
          : pd.isSame(d, "month");
      });
      const map = new Map();

      inRange.forEach((p) => {
        const key = p.id;
        const net = this.netPrice(p);
        const entry = map.get(key) || {
          id: p.id,
          storeName: p.storeName,
          provinceName: p.provinceName || null,
          totalSum: 0,
          netSum: 0,
          commissionSignatures: new Set(),
          percentSum: 0,
          percentCount: 0,
          orderCodesSet: new Set(),
          profitIds: [],
          lockedProfitCount: 0,
          cutoffCount: 0,
          paidCount: 0,
          lastCutoffDate: null,
          lastPaidAt: null,
        };
        if (!entry.provinceName && p.provinceName)
          entry.provinceName = p.provinceName;
        entry.totalSum += Number(p.totalPrice || 0);
        entry.netSum += Number(net || 0);
        if (p.rowId != null) entry.profitIds.push(p.rowId);
        if (p.storeTransferItemId) entry.lockedProfitCount += 1;
        if (p.isCutoff) entry.cutoffCount += 1;
        if (p.isPaid) entry.paidCount += 1;
        if (p.cutoffDate) {
          if (
            !entry.lastCutoffDate ||
            moment(p.cutoffDate).valueOf() >
              moment(entry.lastCutoffDate).valueOf()
          ) {
            entry.lastCutoffDate = p.cutoffDate;
          }
        }
        if (p.paidAt) {
          if (
            !entry.lastPaidAt ||
            moment(p.paidAt).valueOf() > moment(entry.lastPaidAt).valueOf()
          ) {
            entry.lastPaidAt = p.paidAt;
          }
        }
        (p.orderCodes || []).forEach((code) => {
          if (code != null && String(code).trim()) {
            entry.orderCodesSet.add(String(code).trim());
          }
        });
        const sig = `${p.commissionUnit || "percent"}|${Number(
          p.commissionAmount || 0
        )}`;
        entry.commissionSignatures.add(sig);
        if ((p.commissionUnit || "percent") === "percent") {
          const val = Number(p.commissionAmount || 0);
          if (!isNaN(val)) {
            entry.percentSum += val;
            entry.percentCount += 1;
          }
        }
        map.set(key, entry);
      });
      let grouped = Array.from(map.values()).map((e) => {
        let commissionUnit = null;
        let commissionAmount = null;
        if (e.commissionSignatures.size === 1) {
          const [sig] = Array.from(e.commissionSignatures);
          const [unit, amt] = sig.split("|");
          commissionUnit = unit;
          commissionAmount = Number(amt);
        }
        const avgPercent =
          e.percentCount > 0 ? Math.round(e.percentSum / e.percentCount) : null;
        const item = {
          id: e.id,
          storeName: e.storeName,
          provinceName: e.provinceName || null,
          totalPrice: e.totalSum,
          apiNetPrice: e.netSum,
          commissionUnit,
          commissionAmount,
          avgCommissionPercent: avgPercent,
          orderCodes: Array.from(e.orderCodesSet || []),
          profitIds: Array.from(new Set(e.profitIds || [])),
          profitCount: Array.from(new Set(e.profitIds || [])).length,
          lockedProfitCount: Number(e.lockedProfitCount || 0),
          cutoffCount: Number(e.cutoffCount || 0),
          paidCount: Number(e.paidCount || 0),
          lastCutoffDate: e.lastCutoffDate || null,
          lastPaidAt: e.lastPaidAt || null,
        };
        const profitCount = Number(item.profitCount || 0);
        const isFullyCutoff =
          profitCount > 0 && item.cutoffCount === profitCount;
        const isPartiallyCutoff =
          item.cutoffCount > 0 && item.cutoffCount < profitCount;
        const isFullyPaid = profitCount > 0 && item.paidCount === profitCount;
        const isPartiallyPaid =
          item.paidCount > 0 && item.paidCount < profitCount;

        const hasLockedProfits = Number(item.lockedProfitCount || 0) > 0;
        return {
          ...item,
          netPrice: this.netPrice(item),
          totalPriceDisplay: this.asCurrency(item.totalPrice),
          commissionDisplay:
            item.avgCommissionPercent != null
              ? `${item.avgCommissionPercent}%`
              : "—",
          netPriceDisplay: this.asCurrency(this.netPrice(item)),
          isFullyCutoff,
          isPartiallyCutoff,
          isFullyPaid,
          isPartiallyPaid,
          hasLockedProfits,
        };
      });
      if (this.filters.search) {
        const s = this.filters.search.toLowerCase();
        grouped = grouped.filter((p) =>
          (p.storeName || "").toLowerCase().includes(s)
        );
      }
      return grouped;
    },
    overviewSummary() {
      const rows = Array.isArray(this.filteredProfits)
        ? this.filteredProfits
        : [];
      const netTotal = rows.reduce(
        (sum, row) => sum + Number(row.netPrice || 0),
        0
      );
      return {
        netTotalDisplay: this.asCurrency(netTotal),
        lockedProfitCount: rows.reduce(
          (sum, row) => sum + Number(row.lockedProfitCount || 0),
          0
        ),
        openStores: rows.filter(
          (row) =>
            !row.isFullyCutoff && !row.isPartiallyCutoff && !row.isFullyPaid
        ).length,
        partialStores: rows.filter(
          (row) => row.isPartiallyCutoff || row.isPartiallyPaid
        ).length,
        paidStores: rows.filter((row) => row.isFullyPaid).length,
      };
    },
    filteredOrderModalRows() {
      let rows = Array.isArray(this.orderCodesModal.rows)
        ? this.orderCodesModal.rows.slice()
        : [];
      if (this.orderCodesModal.search) {
        const s = String(this.orderCodesModal.search).toLowerCase();
        rows = rows.filter((row) =>
          String(row.orderCode || "")
            .toLowerCase()
            .includes(s)
        );
      }
      switch (this.orderCodesModal.statusFilter) {
        case "open":
          rows = rows.filter((row) => !row.isCutoff && !row.isPaid);
          break;
        case "cutoff_only":
          rows = rows.filter((row) => row.isCutoff && !row.isPaid);
          break;
        case "paid":
          rows = rows.filter((row) => row.isPaid);
          break;
        case "locked":
          rows = rows.filter((row) => !!row.storeTransferItemId);
          break;
        default:
          break;
      }
      return rows;
    },
    selectableOrderRows() {
      return this.filteredOrderModalRows.filter((row) => row.isEditable);
    },
    selectedOrderRows() {
      const selected = new Set(
        (this.orderCodesModal.selectedProfitIds || []).map((id) => Number(id))
      );
      return (this.orderCodesModal.rows || []).filter((row) =>
        selected.has(Number(row.profitId))
      );
    },
    lockedOrderRowsCount() {
      return (this.orderCodesModal.rows || []).filter(
        (row) => !!row.storeTransferItemId
      ).length;
    },
    orderModalSummary() {
      const rows = Array.isArray(this.filteredOrderModalRows)
        ? this.filteredOrderModalRows
        : [];
      const netTotal = rows.reduce(
        (sum, row) => sum + Number(row.netPrice || 0),
        0
      );
      return {
        netTotalDisplay: this.asCurrency(netTotal),
        cutoffCount: rows.filter((row) => !!row.isCutoff).length,
        paidCount: rows.filter((row) => !!row.isPaid).length,
      };
    },
    selectedOrderBulkPrimaryAction() {
      const status = this.orderSelectionGroupStatus(this.selectedOrderRows);
      return this.profitPrimaryActionForStatus(status);
    },
    selectedOrderBulkSecondaryAction() {
      const status = this.orderSelectionGroupStatus(this.selectedOrderRows);
      return this.profitSecondaryActionForStatus(status);
    },
    selectedOrderBulkActionReady() {
      return !!this.orderSelectionGroupStatus(this.selectedOrderRows);
    },
    selectedOrderSelectionHint() {
      const selectedCount = (this.orderCodesModal.selectedProfitIds || []).length;
      if (!selectedCount) return "";
      const selectedRows = this.selectedOrderRows;
      const lockedCount = selectedRows.filter((row) => !row.isEditable).length;
      if (lockedCount > 0) {
        return `${lockedCount} selected order${
          lockedCount > 1 ? "s are" : " is"
        } locked by a transfer batch and cannot be updated here.`;
      }
      const status = this.orderSelectionGroupStatus(selectedRows);
      if (status) return "";
      return "Select orders in the same step before using bulk actions. Bulk updates only support one transition at a time.";
    },
    isLocationSelected() {
      // Data is not province/district-dependent for this API; allow table.
      return true;
    },
    storeOptions() {
      // Use grouped list to offer one row per store
      return this.filteredProfits.map((p) => ({
        id: p.id,
        storeName: p.storeName,
      }));
    },
    provinceOptions() {
      // Map provinceData from store to select options
      return (this.provinceData || []).map((p) => ({
        text: p.province_name_th || p.province_name_en || String(p.province_id),
        value: p.province_id,
      }));
    },
    configDistrictOptions() {
      return this.configDistrictOptionsList;
    },
    activeTransfer() {
      return (
        this.transferRecords.find((r) => r.id === this.activeTransferId) || null
      );
    },
    filteredRecords() {
      let list = this.transferRecords.slice();
      if (this.recordsFilter && this.recordsFilter !== "all") {
        list = list.filter((r) => r.status === this.recordsFilter);
      }
      if (this.recordsSearch) {
        const s = this.recordsSearch.toLowerCase();
        list = list.filter(
          (r) =>
            String(r.id).includes(s) ||
            (r.transferDate && r.transferDate.toLowerCase().includes(s)) ||
            (r.notes && r.notes.toLowerCase().includes(s))
        );
      }
      if (this.recordsStartDate || this.recordsEndDate) {
        const start = this.recordsStartDate
          ? moment(this.recordsStartDate).startOf("day")
          : null;
        const end = this.recordsEndDate
          ? moment(this.recordsEndDate).endOf("day")
          : null;
        list = list.filter((r) => {
          if (!r.transferDate) return false;
          const d = moment(r.transferDate, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD"]);
          if (!d.isValid()) return false;
          if (start && d.isBefore(start)) return false;
          if (end && d.isAfter(end)) return false;
          return true;
        });
      }
      // sort by transferDate desc (fallback by id)
      list.sort((a, b) => {
        const da = a.transferDate ? moment(a.transferDate) : moment(0);
        const db = b.transferDate ? moment(b.transferDate) : moment(0);
        if (db.isSame(da)) return b.id - a.id;
        return db - da;
      });
      return list;
    },
    recordsByDate() {
      const groups = {};
      this.filteredRecords.forEach((r) => {
        const key = r.transferDate
          ? moment(r.transferDate).format("YYYY-MM-DD")
          : "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(r);
      });
      const orderedKeys = Object.keys(groups).sort(
        (a, b) => moment(b).valueOf() - moment(a).valueOf()
      );
      return orderedKeys.map((k) => ({ date: k, items: groups[k] }));
    },
    // Progress ring calculations
    progressRingStyle() {
      if (!this.activeTransfer) return {};
      const circumference = 2 * Math.PI * 85;
      const offset =
        circumference - (this.activeTransfer.progress / 100) * circumference;
      return {
        strokeDasharray: `${circumference} ${circumference}`,
        strokeDashoffset: offset,
        transition: "stroke-dashoffset 0.6s ease",
      };
    },
    completedTasksCount() {
      return this.activeTransfer
        ? this.activeTransfer.tasks.filter((t) => t.status === "completed")
            .length
        : 0;
    },
    inProgressTasksCount() {
      return this.activeTransfer
        ? this.activeTransfer.tasks.filter((t) => t.status === "in_progress")
            .length
        : 0;
    },
    failedTasksCount() {
      return this.activeTransfer
        ? this.activeTransfer.tasks.filter(
            (t) => t.status === "cancelled" || t.status === "failed"
          ).length
        : 0;
    },
    pendingTasksCount() {
      return this.activeTransfer
        ? this.activeTransfer.tasks.filter(
            (t) => t.status === "pending" || t.status === "paused"
          ).length
        : 0;
    },
    // Multi-step wizard
    formDistrictOptions() {
      return this.form.province
        ? this.districtsByProvince[this.form.province] || []
        : [];
    },
    eligibleStores() {
      if (!this.form.province || !this.form.district) return [];
      const min = Number(this.form.minNetPrice || 0);
      return this.storeOptions.filter((s) => {
        const full = this.filteredProfits.find((p) => p.id === s.id);
        return full && this.netPrice(full) >= min;
      });
    },
    totalEligibleProfit() {
      return this.eligibleStores.reduce((sum, s) => {
        const full = this.filteredProfits.find((p) => p.id === s.id);
        return sum + (full ? this.netPrice(full) : 0);
      }, 0);
    },
    avgCommission() {
      if (this.eligibleStores.length === 0) return 0;
      const values = this.eligibleStores
        .map((s) => this.filteredProfits.find((p) => p.id === s.id))
        .filter(
          (p) =>
            p && p.commissionUnit === "percent" && p.commissionAmount != null
        )
        .map((p) => Number(p.commissionAmount));
      if (values.length === 0) return 0;
      const total = values.reduce((sum, v) => sum + v, 0);
      return Math.round(total / values.length);
    },
    selectedStoresTotal() {
      return this.form.selectedStoreIds.reduce((sum, id) => {
        const store = this.filteredProfits.find((p) => p.id === id);
        return sum + (store ? this.netPrice(store) : 0);
      }, 0);
    },
    canProceedToNextStep() {
      if (this.currentStep === 1) {
        return this.form.province && this.form.district;
      }
      if (this.currentStep === 2) {
        return this.form.selectedStoreIds.length > 0;
      }
      return true;
    },
  },
  created() {
    this.restoreConfig();
    // Load provinces from Vuex store
    this.getProvinceOnUse().then(() => {
      // Optionally preset config location if none
      if (!this.configProvince && this.provinceOptions.length) {
        this.configProvince = this.provinceOptions[0].value;
      }
    });
    this.fetchProfitsFromAPI()
      .then(() => {
        this.buildTrend();
        this.computeDashboard();
      })
      .finally(() => {
        this.loadingProfits = false;
        // Initialize auto scheduler
        this.loadLastAutoRunMap();
        this.ensureAutoRunner();
      });
    // Initial fetch of transfer records timeline
    this.fetchTransferRecords();
    // Fetch latest store transfer items for admin
    this.fetchStoreTransferItems({
      page: this.storeTransferPagination.page,
      page_size: this.storeTransferPagination.rowsPerPage,
    }).finally(() => {
      this.storeTransferPaginationReady = true;
    });
  },
  beforeDestroy() {
    if (this.progressTimer) clearInterval(this.progressTimer);
    if (this.autoRunnerTimer) clearInterval(this.autoRunnerTimer);
  },
  watch: {
    "menus.scheduleTime"(open) {
      if (open) this.tempScheduleTime = this.config.scheduleTime;
    },
    "filters.selectedProvince"(nv) {
      // Province filter only (district filter removed)
      this.fetchTransferRecords();
      if (this.filters.date) this.reloadCurrentProfitRows();
    },
    "filters.viewMode"() {
      if (this.filters.date) this.reloadCurrentProfitRows();
    },
    "filters.date"() {
      this.reloadCurrentProfitRows();
    },
    configProvince(nv) {
      // Fetch district list when province changes for config panel
      this.configDistrict = null;
      this.loadConfigForLocation();
      if (!nv) {
        this.configDistrictOptionsList = [];
        return;
      }
      this.getDistrictCore(nv).then((res) => {
        const options = Array.isArray(res)
          ? res.map((d) => ({
              text:
                d.district_name_th ||
                d.district_name_en ||
                String(d.id || d.district_id),
              value: d.district_id || d.id,
            }))
          : [];
        this.configDistrictOptionsList = options;
      });
    },
    configDistrict() {
      this.loadConfigForLocation();
    },
    // Auto-save on config changes
    "config.autoRecord"(nv) {
      // If turning on, ensure schedule prerequisites are set
      if (nv) {
        if (!this.validateRecordPrereqs()) {
          this.notify(
            "Set schedule time (and day for monthly) before enabling auto record",
            "warning"
          );
          this.guardingAutoRecordToggle = true;
          this.config.autoRecord = false;
          this.$nextTick(() => {
            this.guardingAutoRecordToggle = false;
          });
          return;
        }
      }
      if (!this.guardingAutoRecordToggle) this.queueSaveConfig();
    },
    "config.autoTransfer"() {
      this.queueSaveConfig();
    },
    "config.frequency"() {
      this.queueSaveConfig();
    },
    "config.scheduleDate"() {
      this.queueSaveConfig();
    },
    "config.scheduleTime"() {
      this.queueSaveConfig();
    },
    "form.selectedStoreIds"() {
      // Ensure amounts initialized
      this.form.selectedStoreIds.forEach((id) => {
        if (this.form.amounts[id] == null) {
          const store = this.profits.find((p) => p.id === id);
          this.$set(this.form.amounts, id, store ? this.netPrice(store) : 0);
        }
      });
      // Cleanup removed ids
      Object.keys(this.form.amounts).forEach((id) => {
        if (!this.form.selectedStoreIds.includes(+id))
          this.$delete(this.form.amounts, id);
      });
    },
    "form.autoCalculate"(val) {
      if (val) {
        this.form.selectedStoreIds.forEach((id) => {
          const store = this.profits.find((p) => p.id === id);
          this.$set(this.form.amounts, id, store ? this.netPrice(store) : 0);
        });
      }
    },

    storeTransferPagination: {
      deep: true,
      handler(p) {
        if (!this.storeTransferPaginationReady) return;
        const page = Number((p && p.page) || 1);
        const pageSize = Number((p && p.rowsPerPage) || 20);
        this.fetchStoreTransferItems({
          page,
          page_size: pageSize,
          ...this.storeTransferQueryFilters(),
        });
      },
    },
  },
  methods: {
    ...mapActions({
      getStoreProfit: "fetchMerchantStoreProfit/getStoreProfit",
      updateStoreProfitStatus:
        "fetchMerchantStoreProfit/updateStoreProfitStatus",
      getProvinceOnUse: "fetchProvinceBack/getData",
      getDistrictCore: "fetchProvinceBack/getDistrictCore",
      getTransferConfigByDistrict: "StoreTransferConfig/getByDistrict",
      patchTransferConfig: "StoreTransferConfig/patch",
      fetchTransferBatches: "StoreTransferBatch/fetch",
      updateTransferBatchStatus: "StoreTransferBatch/updateStatus",
      fetchTransferItemById: "StoreTransferBatch/fetchTransferItemById",
      // StoreTransferItem module
      fetchStoreTransferItems: "StoreTransferItem/fetch",
      updateStoreTransferItemBank: "StoreTransferItem/updateBank",
      retryStoreTransferItem: "StoreTransferItem/retryItem",
    }),
    // Utils
    asCurrency(n) {
      const v = Number(n || 0);
      return `฿${v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    prettyJson(val) {
      try {
        return JSON.stringify(val, null, 2);
      } catch (e) {
        return val == null ? "" : String(val);
      }
    },
    flashpayTradeStatusLabel(tradeStatus) {
      const n = Number(tradeStatus);
      if (n === 3) return "success";
      if (n === 2) return "processing";
      if (n === 4) return "failed";
      return "unknown";
    },
    flashpayTradeStatusColor(tradeStatus) {
      const n = Number(tradeStatus);
      if (n === 3) return "success";
      if (n === 2) return "info";
      if (n === 4) return "error";
      return "grey";
    },
    formatDate(d) {
      return d ? moment(d).format("YYYY-MM-DD") : "";
    },
    formatDateTime(d) {
      if (!d) return "-";
      const m = moment(d);
      if (!m.isValid()) return "-";
      return m.format("YYYY-MM-DD HH:mm");
    },
    maskBankAccountNumber(val) {
      if (!val) return "-";
      const s = String(val).replace(/\s+/g, "");
      if (s.length <= 4) return s;
      const tail = s.slice(-4);
      return `••••••${tail}`;
    },
    storeTransferStatusColor(status) {
      const s = String(status || "").toLowerCase();
      if (s === "success") return "green";
      if (s === "failed") return "red";
      if (s === "in_progress") return "blue";
      if (s === "pending") return "grey";
      if (s === "cancelled") return "grey darken-2";
      return "grey";
    },
    storeTransferBatchRecordStatus(item) {
      if (!item) return "-";
      if (item.batch_record_status) return item.batch_record_status;
      if (item.batch && item.batch.record_status)
        return item.batch.record_status;
      return "-";
    },
    getProvinceNameById(provinceId) {
      if (provinceId == null) return null;
      const id = Number(provinceId);
      const p = (this.provinceData || []).find(
        (x) => Number(x.province_id) === id
      );
      if (!p) return null;
      return (
        p.province_name_en ||
        p.province_name_th ||
        (p.province_id != null ? String(p.province_id) : null)
      );
    },
    storeTransferProvinceName(item) {
      const provinceId =
        (item && item.batch && item.batch.province_id) ||
        item.province_id ||
        null;
      const name = this.getProvinceNameById(provinceId);
      return name || (provinceId != null ? `#${provinceId}` : "-");
    },
    storeTransferQueryFilters() {
      const f = this.storeTransferFilters || {};
      const toInt = (v) => {
        if (v == null) return null;
        const s = String(v).trim();
        if (!s) return null;
        const n = Number(s);
        return Number.isFinite(n) ? Math.trunc(n) : null;
      };
      const batchId = toInt(f.batch_id);
      const merchantId = toInt(f.merchant_id);
      const storeId = toInt(f.store_id);
      const params = {};
      if (batchId != null) params.batch_id = batchId;
      if (merchantId != null) params.merchant_id = merchantId;
      if (storeId != null) params.store_id = storeId;
      if (f.status) params.status = f.status;
      if (f.record_status) params.record_status = f.record_status;
      return params;
    },
    applyStoreTransferFilters() {
      this.storeTransferPagination.page = 1;
      return this.refreshStoreTransferItems();
    },
    resetStoreTransferFilters() {
      this.storeTransferFilters = {
        batch_id: "",
        merchant_id: "",
        store_id: "",
        status: null,
        record_status: null,
      };
      this.storeTransferPagination.page = 1;
      return this.refreshStoreTransferItems();
    },
    profitStatusFromFlags(item) {
      if (!item) return null;
      if (item.isPaid) return "paid";
      if (item.isCutoff) return "cutoff";
      return "new";
    },
    storeProfitGroupStatus(item) {
      if (!item || !Number(item.profitCount || 0)) return null;
      if (item.isFullyPaid) return "paid";
      if (item.isFullyCutoff && !item.isFullyPaid) return "cutoff";
      if (!item.cutoffCount && !item.paidCount) return "new";
      return null;
    },
    orderSelectionGroupStatus(rows) {
      if (!Array.isArray(rows) || rows.length === 0) return null;
      const editableRows = rows.filter((row) => row && row.isEditable);
      if (editableRows.length !== rows.length) return null;
      const statuses = Array.from(
        new Set(editableRows.map((row) => this.profitStatusFromFlags(row)))
      ).filter(Boolean);
      return statuses.length === 1 ? statuses[0] : null;
    },
    profitPrimaryActionForStatus(status) {
      switch (status) {
        case "new":
          return {
            key: "cutoff",
            label: "Mark Cutoff",
            color: "info",
            payload: { is_cutoff: true },
          };
        case "cutoff":
          return {
            key: "paid",
            label: "Mark Payout",
            color: "success",
            payload: { is_paid: true },
          };
        case "paid":
          return {
            key: "paid",
            label: "Undo Payout",
            color: "success",
            outline: true,
            payload: { is_paid: false },
          };
        default:
          return null;
      }
    },
    profitSecondaryActionForStatus(status) {
      if (status !== "cutoff") return null;
      return {
        key: "cutoff",
        label: "Undo Cutoff",
        color: "info",
        outline: true,
        payload: { is_cutoff: false },
      };
    },
    storeProfitPrimaryAction(item) {
      return this.profitPrimaryActionForStatus(this.storeProfitGroupStatus(item));
    },
    storeProfitSecondaryAction(item) {
      return this.profitSecondaryActionForStatus(
        this.storeProfitGroupStatus(item)
      );
    },
    orderProfitPrimaryAction(row) {
      return this.profitPrimaryActionForStatus(this.profitStatusFromFlags(row));
    },
    orderProfitSecondaryAction(row) {
      return this.profitSecondaryActionForStatus(
        this.profitStatusFromFlags(row)
      );
    },
    storeProfitStatusGuide(item) {
      const status = this.storeProfitGroupStatus(item);
      if (status === "new") return "Next step: mark cutoff before payout.";
      if (status === "cutoff") {
        return "Next step: mark payout, or undo cutoff to return to new.";
      }
      if (status === "paid") {
        return "Undo payout first before returning to new.";
      }
      if (item && item.profitCount) {
        return "Mixed statuses detected. Use Inspect Details to update one step at a time.";
      }
      return "";
    },
    async runStoreProfitAction(item, action) {
      if (!action) return;
      return this.toggleStoreProfitStatus(item, action);
    },
    async runOrderProfitAction(row, action) {
      if (!action) return;
      return this.updateSingleOrderModalStatus(row, action);
    },
    async runBulkOrderProfitAction(action) {
      if (!action) return;
      return this.bulkUpdateOrderModalStatus(action);
    },
    openOrderCodesModal(item) {
      const rows = (this.profits || [])
        .filter((row) => item && Number(row.id) === Number(item.id))
        .map((row) => {
          const commissionDisplay =
            row.commissionUnit === "percent"
              ? `${Number(row.commissionAmount || 0)}%`
              : this.asCurrency(row.commissionAmount || 0);
          return {
            profitId: row.rowId,
            orderCode: row.orderCode || "-",
            orderDateTime: row.orderDateTime || row.date,
            totalPrice: Number(row.totalPrice || 0),
            commissionDisplay,
            netPrice: Number(this.netPrice(row) || 0),
            isCutoff: !!row.isCutoff,
            cutoffDate: row.cutoffDate || null,
            isPaid: !!row.isPaid,
            paidAt: row.paidAt || null,
            storeTransferItemId: row.storeTransferItemId || null,
            isEditable: !row.storeTransferItemId,
          };
        })
        .sort((a, b) => {
          const da = a.orderDateTime ? moment(a.orderDateTime).valueOf() : 0;
          const db = b.orderDateTime ? moment(b.orderDateTime).valueOf() : 0;
          return db - da;
        });
      const normalized = rows.map((row) => row.orderCode).filter(Boolean);
      this.orderCodesModal.storeId = item ? item.id : null;
      this.orderCodesModal.storeName = item ? item.storeName || "" : "";
      this.orderCodesModal.orderCodes = normalized.sort();
      this.orderCodesModal.rows = rows;
      this.orderCodesModal.search = "";
      this.orderCodesModal.statusFilter = "all";
      this.orderCodesModal.selectedProfitIds = [];
      this.orderCodesModal.bulkLoading = {
        cutoff: false,
        paid: false,
        cutoffUndo: false,
        paidUndo: false,
      };
      this.orderCodesModal.rowLoadingByKey = {};
      this.orderCodesModal.show = true;
    },
    mapStoreProfitRows(rows, fallbackDate) {
      return (rows || []).map((row) => {
        const name =
          row.store_name_th ||
          row.store_name_en ||
          `Store ${row.store_source_id}`;
        const provinceId = row.province_id || row.provinceId || null;
        const provinceName =
          row.province_name_th ||
          row.province_name_en ||
          (() => {
            if (!provinceId) return null;
            const p = (this.provinceData || []).find(
              (x) => x.province_id === provinceId
            );
            return p
              ? p.province_name_th || p.province_name_en || String(provinceId)
              : String(provinceId);
          })();
        const total = Number(row.total_product_price || 0);
        const commissionAmt = Number(row.commission_amount || 0);
        const net = Number(row.net_price || 0);
        const created =
          row.store_profit_source_order_date_time ||
          row.created_at ||
          row.updated_at ||
          fallbackDate;
        const orderCodes = (() => {
          const raw = row.store_profit_source_order_code;
          if (Array.isArray(raw)) return raw.filter(Boolean);
          if (raw != null && String(raw).trim()) return [String(raw).trim()];
          return [];
        })();
        return {
          rowId: row.id,
          id: row.store_source_id,
          storeName: name,
          provinceId,
          provinceName,
          totalPrice: total,
          commissionUnit: row.commission_unit || "percent",
          commissionAmount: commissionAmt,
          apiNetPrice: isNaN(net) ? null : net,
          date: moment(created).format("YYYY-MM-DD"),
          orderCodes,
          isCutoff: !!row.is_cutoff,
          cutoffDate: row.cutoff_date || null,
          isPaid: !!row.is_paid,
          paidAt: row.paid_at || null,
          storeTransferItemId: row.store_transfer_item_id || null,
          orderCode:
            row.store_profit_source_order_code != null
              ? String(row.store_profit_source_order_code).trim()
              : "",
          orderDateTime: created,
        };
      });
    },
    async reloadCurrentProfitRows() {
      if (!this.filters.date) return;
      const d = moment(this.filters.date);
      let end;
      let start;

      if (this.filters.viewMode === "daily") {
        end = d.format("YYYY-MM-DD");
        start = end;
      } else {
        start = d.clone().startOf("month").format("YYYY-MM-DD");
        end = d.clone().endOf("month").format("YYYY-MM-DD");
      }

      this.loadingProfits = true;
      try {
        const res = await this.getStoreProfit({
          start_date: start,
          end_date: end,
          page: 1,
          page_size: 500,
          ...(() => {
            const sel = (this.provinceData || []).find(
              (p) => p.province_id === this.filters.selectedProvince
            );
            return sel
              ? {
                  province_name: sel.province_name_en || sel.province_name_th,
                }
              : {};
          })(),
        });
        if (res && (res.msg || Array.isArray(res))) {
          const rows = res.msg ? res.data || [] : res;

          this.profits = this.mapStoreProfitRows(rows, end);
        }
      } finally {
        this.loadingProfits = false;
      }
    },
    storeProfitActionKey(item, action) {
      return `${action}:${item && item.id ? item.id : "unknown"}`;
    },
    isStoreProfitActionLoading(item, action) {
      return !!this.storeProfitActionLoadingByKey[
        this.storeProfitActionKey(item, action)
      ];
    },
    isStoreProfitActionBusy(item) {
      return (
        this.isStoreProfitActionLoading(item, "cutoff") ||
        this.isStoreProfitActionLoading(item, "paid")
      );
    },
    isStoreProfitActionDisabled(item) {
      return (
        this.isStoreProfitActionBusy(item) ||
        !item ||
        !Array.isArray(item.profitIds) ||
        item.profitIds.length === 0 ||
        !!item.hasLockedProfits
      );
    },
    storeProfitLockHint(item) {
      const lockedCount = Number(
        item && item.lockedProfitCount ? item.lockedProfitCount : 0
      );
      if (!lockedCount) return "";
      return `${lockedCount} order${
        lockedCount > 1 ? "s are" : " is"
      } already linked to a transfer batch`;
    },
    storeProfitCutoffColor(item) {
      if (!item || !item.profitCount) return "grey";
      if (item.isFullyCutoff) return "info";
      if (item.isPartiallyCutoff) return "warning";
      return "grey";
    },
    storeProfitPaidColor(item) {
      if (!item || !item.profitCount) return "grey";
      if (item.isFullyPaid) return "success";
      if (item.isPartiallyPaid) return "warning";
      return "grey";
    },
    storeProfitCutoffLabel(item) {
      if (!item || !item.profitCount) return "No profits";
      if (item.isFullyCutoff) return "Cutoff complete";
      if (item.isPartiallyCutoff) return "Cutoff partial";
      return "Not cutoff";
    },
    storeProfitPaidLabel(item) {
      if (!item || !item.profitCount) return "No profits";
      if (item.isFullyPaid) return "Payout complete";
      if (item.isPartiallyPaid) return "Payout partial";
      return "Not paid";
    },
    storeProfitCutoffHint(item) {
      if (!item || !item.profitCount) return "0/0 orders";
      const count = `${item.cutoffCount}/${item.profitCount} orders`;
      if (item.lastCutoffDate) {
        return `${count} • ${moment(item.lastCutoffDate).format("YYYY-MM-DD")}`;
      }
      return count;
    },
    storeProfitPaidHint(item) {
      if (!item || !item.profitCount) return "0/0 orders";
      const count = `${item.paidCount}/${item.profitCount} orders`;
      if (item.lastPaidAt) {
        return `${count} • ${this.formatDateTime(item.lastPaidAt)}`;
      }
      return count;
    },
    async toggleStoreProfitStatus(item, action) {
      if (
        !item ||
        !Array.isArray(item.profitIds) ||
        item.profitIds.length === 0
      ) {
        this.notify("No store profit rows found for this store", "warning");
        return;
      }
      if (item.hasLockedProfits) {
        this.notify(this.storeProfitLockHint(item), "warning");
        return;
      }
      if (!action || !action.payload) return;
      const key = this.storeProfitActionKey(item, action.key);
      this.$set(this.storeProfitActionLoadingByKey, key, true);
      try {
        const payload = {
          profit_ids: item.profitIds,
          ...action.payload,
        };
        const res = await this.updateStoreProfitStatus(payload);
        if (res && res.msg) {
          this.notify("Store profit status updated", "success");
          await this.reloadCurrentProfitRows();
        } else {
          this.notify(
            (res && res.error) || "Failed to update store profit status",
            "error"
          );
        }
      } finally {
        this.$set(this.storeProfitActionLoadingByKey, key, false);
      }
    },
    orderModalRowActionKey(profitId, action) {
      return `${action}:${profitId}`;
    },
    isOrderModalRowLoading(profitId, action) {
      return !!this.orderCodesModal.rowLoadingByKey[
        this.orderModalRowActionKey(profitId, action)
      ];
    },
    isOrderModalRowBusy(profitId) {
      return (
        this.isOrderModalRowLoading(profitId, "cutoff") ||
        this.isOrderModalRowLoading(profitId, "paid")
      );
    },
    isOrderModalSelected(profitId) {
      return this.orderCodesModal.selectedProfitIds.includes(Number(profitId));
    },
    toggleOrderModalSelection(row) {
      if (!row || !row.isEditable) return;
      const id = Number(row.profitId);
      const has = this.orderCodesModal.selectedProfitIds.includes(id);
      this.orderCodesModal.selectedProfitIds = has
        ? this.orderCodesModal.selectedProfitIds.filter((x) => x !== id)
        : this.orderCodesModal.selectedProfitIds.concat(id);
    },
    selectAllOrderModalRows() {
      this.orderCodesModal.selectedProfitIds = this.selectableOrderRows.map(
        (row) => Number(row.profitId)
      );
    },
    clearSelectedOrderModalRows() {
      this.orderCodesModal.selectedProfitIds = [];
    },
    async updateSingleOrderModalStatus(row, action) {
      if (!row || !row.isEditable) return;
      if (!action || !action.payload) return;
      const key = this.orderModalRowActionKey(row.profitId, action.key);
      this.$set(this.orderCodesModal.rowLoadingByKey, key, true);
      try {
        const res = await this.updateStoreProfitStatus({
          profit_ids: [row.profitId],
          ...action.payload,
        });
        if (res && res.msg) {
          this.notify("Order status updated", "success");
          await this.reloadCurrentProfitRows();
          const refreshed = this.filteredProfits.find(
            (profit) =>
              Number(profit.id) === Number(this.orderCodesModal.storeId)
          );
          if (refreshed) this.openOrderCodesModal(refreshed);
        } else {
          this.notify(
            (res && res.error) || "Failed to update order status",
            "error"
          );
        }
      } finally {
        this.$set(this.orderCodesModal.rowLoadingByKey, key, false);
      }
    },
    async bulkUpdateOrderModalStatus(action) {
      if (!action || !action.payload) return;
      const profitIds = (this.orderCodesModal.selectedProfitIds || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
      if (profitIds.length === 0) {
        this.notify("Please select order rows first", "warning");
        return;
      }
      if (!this.selectedOrderBulkActionReady) {
        this.notify(
          "Select orders in the same step before using bulk actions",
          "warning"
        );
        return;
      }
      const loadingKey = action.key;
      this.$set(this.orderCodesModal.bulkLoading, loadingKey, true);
      try {
        const res = await this.updateStoreProfitStatus({
          profit_ids: profitIds,
          ...action.payload,
        });
        if (res && res.msg) {
          this.notify("Selected orders updated", "success");
          await this.reloadCurrentProfitRows();
          const refreshed = this.filteredProfits.find(
            (profit) =>
              Number(profit.id) === Number(this.orderCodesModal.storeId)
          );
          if (refreshed) this.openOrderCodesModal(refreshed);
        } else {
          this.notify(
            (res && res.error) || "Failed to update selected orders",
            "error"
          );
        }
      } finally {
        this.$set(this.orderCodesModal.bulkLoading, loadingKey, false);
      }
    },
    // Store Transfer Item helpers
    openEditBank(item) {
      if (!item) return;
      this.editBankForm = {
        item_id: item.id,
        bank_account_number: item.bank_account_number || "",
        bank_account_name: item.bank_account_name || "",
        bank_code: item.bank_code || "",
        store_bank_id: item.store_bank_id || null,
        store_bank_bank_id: item.store_bank_bank_id || null,
      };
      this.storeTransferDialog.show = true;
    },

    refreshStoreTransferItems() {
      return this.fetchStoreTransferItems({
        page: this.storeTransferPagination.page,
        page_size: this.storeTransferPagination.rowsPerPage,
        ...this.storeTransferQueryFilters(),
      });
    },

    async submitEditBank() {
      const body = { ...this.editBankForm };
      if (!body.item_id) return this.notify("Missing item id", "error");
      const res = await this.updateStoreTransferItemBank(body);
      if (res) {
        this.notify("Bank details saved", "success");
        this.storeTransferDialog.show = false;
        // If user is looking at the same transfer item, update it in-place.
        if (
          this.transferItemModal &&
          this.transferItemModal.show &&
          this.transferItemModal.item &&
          Number(this.transferItemModal.item.id) === Number(res.id)
        ) {
          this.transferItemModal.item = {
            ...this.transferItemModal.item,
            ...res,
          };
          if (
            this.transferItemModal.task &&
            Number(this.transferItemModal.task.itemId) === Number(res.id)
          ) {
            this.$set(
              this.transferItemModal.task,
              "storeBankId",
              res.store_bank_id || this.transferItemModal.task.storeBankId
            );
          }
        }
        this.refreshStoreTransferItems();
      } else {
        this.notify("Failed to save bank details", "error");
      }
    },

    async onRetry(item) {
      if (!item || !item.id) return;
      if (this.storeTransferRetryLoadingById[item.id]) return;
      this.$set(this.storeTransferRetryLoadingById, item.id, true);
      try {
        const payload = { item_id: item.id, run_now: true };
        const res = await this.retryStoreTransferItem(payload);
        if (res && res.msg) {
          this.notify("Retry requested", "success");
          await this.refreshStoreTransferItems();
        } else {
          this.notify("Retry failed", "error");
        }
      } finally {
        this.$set(this.storeTransferRetryLoadingById, item.id, false);
      }
    },
    // Validate schedule prerequisites for enabling auto record
    validateRecordPrereqs() {
      const type = this.config.frequency;
      const time = this.config.scheduleTime;
      // type must be 'daily' or 'monthly'
      if (type !== "daily" && type !== "monthly") return false;
      // time must be HH:mm and valid
      if (!this.isValidHHMM(time)) return false;
      if (type === "monthly") {
        const d = Number(this.config.scheduleDate);
        if (!Number.isInteger(d) || d < 1 || d > 31) return false;
      }
      return true;
    },
    isValidHHMM(val) {
      if (!val || typeof val !== "string") return false;
      const m = val.match(/^([0-9]{1,2}):([0-9]{2})$/);
      if (!m) return false;
      const h = Number(m[1]);
      const min = Number(m[2]);
      return h >= 0 && h <= 23 && min >= 0 && min <= 59;
    },
    // Convert local HH:mm to UTC HH:mm:ss for API
    toUtcTimeStr(localHHMM) {
      if (!localHHMM) return null;
      const [hStr, mStr] = String(localHHMM).split(":");
      let h = parseInt(hStr || "0", 10);
      let m = parseInt(mStr || "0", 10);
      if (isNaN(h)) h = 0;
      if (isNaN(m)) m = 0;
      const offsetMin = new Date().getTimezoneOffset(); // minutes to add to local to reach UTC
      let totalMin = h * 60 + m + offsetMin; // UTC = local + offset
      // normalize to 0..1439
      totalMin = ((totalMin % 1440) + 1440) % 1440;
      const uh = Math.floor(totalMin / 60);
      const um = totalMin % 60;
      const hh = String(uh).padStart(2, "0");
      const mm = String(um).padStart(2, "0");
      return `${hh}:${mm}:00`;
    },
    // Convert UTC HH:mm[:ss] to local HH:mm for display
    fromUtcTimeStr(utcHHMMSS) {
      if (!utcHHMMSS) return "";
      const parts = String(utcHHMMSS).split(":");
      let h = parseInt(parts[0] || "0", 10);
      let m = parseInt(parts[1] || "0", 10);
      if (isNaN(h)) h = 0;
      if (isNaN(m)) m = 0;
      const offsetMin = new Date().getTimezoneOffset(); // minutes to add to local to reach UTC
      // local = UTC - offset
      let totalMin = h * 60 + m - offsetMin;
      totalMin = ((totalMin % 1440) + 1440) % 1440;
      const lh = Math.floor(totalMin / 60);
      const lm = totalMin % 60;
      return `${String(lh).padStart(2, "0")}:${String(lm).padStart(2, "0")}`;
    },
    netPrice(item) {
      if (typeof item.apiNetPrice === "number") {
        return Math.round(item.apiNetPrice * 100) / 100;
      }
      if (item.commissionUnit === "percent") {
        const rate = Number(item.commissionAmount || 0);
        return Math.round(item.totalPrice * (1 - rate / 100) * 100) / 100;
      }
      if (item.commissionUnit === "flat") {
        return Math.max(
          0,
          Math.round((item.totalPrice - (item.commissionAmount || 0)) * 100) /
            100
        );
      }
      return Math.round(Number(item.totalPrice || 0) * 100) / 100;
    },
    prettyStatus(s) {
      const map = {
        pending: "Pending",
        in_progress: "In Progress",
        completed: "Completed",
        paused: "Paused",
        cancelled: "Cancelled",
        // Back-compat if any legacy 'failed' values exist locally
        failed: "Cancelled",
      };
      return map[s] || s;
    },
    statusColor(s) {
      const map = {
        pending: "grey",
        in_progress: "info",
        completed: "success",
        paused: "warning",
        cancelled: "error",
        // Back-compat
        failed: "error",
      };
      return map[s] || "grey";
    },

    // Visual helpers
    avatarGradient(seed) {
      const colors = [
        ["#9333EA", "#22D3EE"],
        ["#F59E0B", "#84CC16"],
        ["#06B6D4", "#3B82F6"],
        ["#EF4444", "#F97316"],
      ];
      const pair = colors[seed % colors.length];
      return `background: linear-gradient(135deg, ${pair[0]}, ${pair[1]});`;
    },
    // Location helpers
    locationKey(province, district) {
      return `${province || ""}|${district || ""}`;
    },
    async loadConfigForLocation() {
      const key = this.locationKey(this.configProvince, this.configDistrict);
      // Start hydration: prevent auto-save and clear any pending save timers
      this.isHydratingConfig = true;
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
      }
      const existing = this.configMap[key];
      if (existing) {
        this.config = Object.assign({}, this.config, existing);
      }
      // Fetch from API when district selected; silently pass if not found
      if (this.configDistrict) {
        try {
          const data = await this.getTransferConfigByDistrict({
            district_id: this.configDistrict,
          });
          if (data) {
            // Map API fields to local config
            const frequency = data.auto_transfer_type || "daily";
            let scheduleTime = "";
            if (data.schedule_time) {
              scheduleTime = this.fromUtcTimeStr(data.schedule_time);
            }
            let scheduleDate = null;
            if (frequency === "monthly" && data.schedule_date != null) {
              scheduleDate = Math.max(
                1,
                Math.min(31, Number(data.schedule_date))
              );
            }
            this.config = Object.assign({}, this.config, {
              autoTransfer: !!data.is_auto_transfer_enabled,
              autoRecord: !!data.transfer_record_enabled,
              frequency,
              scheduleTime,
              scheduleDate,
            });
            // Cache per location
            this.$set(this.configMap, key, this.config);
            // Store server config baseline for diffing on PATCH
            this.lastServerConfig = {
              district_id: data.district_id || this.configDistrict,
              province_id: data.province_id || this.configProvince,
              is_auto_transfer_enabled: !!data.is_auto_transfer_enabled,
              transfer_record_enabled: !!data.transfer_record_enabled,
              auto_transfer_type: frequency,
              schedule_time: data.schedule_time || null,
              schedule_date:
                frequency === "monthly" && data.schedule_date != null
                  ? Number(data.schedule_date)
                  : null,
            };
          }
        } catch (e) {
          this.notify("Failed to load transfer config", "error");
        }
      }
      // End hydration: re-enable auto-save for user changes
      // Use a short barrier to avoid any late UI emits triggering PATCH
      this.hydratingUntil = Date.now() + 800;
      this.isHydratingConfig = false;
    },

    async fetchProfitsFromAPI() {
      try {
        // Initial load: current month range for graph and today stats
        const end = moment().format("YYYY-MM-DD");
        const start = moment().startOf("month").format("YYYY-MM-DD");
        // Determine selected province name from Vuex data if available
        const selectedProvinceObj = (this.provinceData || []).find(
          (p) => p.province_id === this.filters.selectedProvince
        );
        const provinceNameParam = selectedProvinceObj
          ? {
              province_name:
                selectedProvinceObj.province_name_en ||
                selectedProvinceObj.province_name_th,
            }
          : {};
        const res = await this.getStoreProfit({
          start_date: start,
          end_date: end,
          page: 1,
          page_size: 500,
          ...provinceNameParam,
        });
        if (res && res.msg) {
          const mapped = (res.data || []).map((row) => {
            const name =
              row.store_name_th ||
              row.store_name_en ||
              `Store ${row.store_source_id}`;
            const provinceId = row.province_id || row.provinceId || null;
            const provinceName =
              row.province_name_th ||
              row.province_name_en ||
              (() => {
                if (!provinceId) return null;
                const p = (this.provinceData || []).find(
                  (x) => x.province_id === provinceId
                );
                return p
                  ? p.province_name_th ||
                      p.province_name_en ||
                      String(provinceId)
                  : String(provinceId);
              })();
            const total = Number(row.total_product_price || 0);
            const commissionAmt = Number(row.commission_amount || 0);
            const net = Number(row.net_price || 0);
            const created = row.created_at || row.updated_at || null;
            return {
              id: row.store_source_id,
              storeName: name,
              provinceId,
              provinceName,
              totalPrice: total,
              commissionUnit: row.commission_unit || "percent",
              commissionAmount: commissionAmt,
              apiNetPrice: isNaN(net) ? null : net,
              date: created ? moment(created).format("YYYY-MM-DD") : end,
            };
          });
          this.incomeProfits = mapped;
        } else if (Array.isArray(res)) {
          // merchantInstance may already unwrap data directly
          const mapped = (res || []).map((row) => {
            const name =
              row.store_name_th ||
              row.store_name_en ||
              `Store ${row.store_source_id}`;
            const provinceId = row.province_id || row.provinceId || null;
            const provinceName =
              row.province_name_th ||
              row.province_name_en ||
              (() => {
                if (!provinceId) return null;
                const p = (this.provinceData || []).find(
                  (x) => x.province_id === provinceId
                );
                return p
                  ? p.province_name_th ||
                      p.province_name_en ||
                      String(provinceId)
                  : String(provinceId);
              })();
            const total = Number(row.total_product_price || 0);
            const commissionAmt = Number(row.commission_amount || 0);
            const net = Number(row.net_price || 0);
            const created = row.created_at || row.updated_at || null;
            return {
              id: row.store_source_id,
              storeName: name,
              provinceId,
              provinceName,
              totalPrice: total,
              commissionUnit: row.commission_unit || "percent",
              commissionAmount: commissionAmt,
              apiNetPrice: isNaN(net) ? null : net,
              date: created ? moment(created).format("YYYY-MM-DD") : end,
            };
          });
          this.incomeProfits = mapped;
        }
      } catch (e) {
        // Keep mock-less gracefully; show notification
        this.notify("Failed to load store profit data", "error");
      }
    },

    // Fetch transfer records timeline from API via Vuex
    async fetchTransferRecords() {
      const requestId = (this._recordsRequestId =
        (this._recordsRequestId || 0) + 1);
      this.recordsLoading = true;
      try {
        const params = {
          page: 1,
          page_size: 20,
          order: "start_date",
        };
        if (this.filters.selectedProvince)
          params.province_id = this.filters.selectedProvince;
        const rows = await this.fetchTransferBatches(params);
        if (this._recordsRequestId !== requestId) return;
        const normalizeEntry = (entry) => {
          const isObj = entry && typeof entry === "object";
          const storeId = isObj
            ? entry.store_id ||
              entry.store_source_id ||
              entry.source_id ||
              entry.id
            : entry;
          const itemId = isObj
            ? entry.item_id ||
              entry.transfer_item_id ||
              entry.store_transfer_item_id ||
              (entry.id && entry.store_id && entry.id !== entry.store_id
                ? entry.id
                : null)
            : null;
          const name = isObj
            ? entry.store_name_th ||
              entry.store_name_en ||
              entry.store_name ||
              `Store ${storeId}`
            : String(entry);
          const net = isObj
            ? Number(entry.net_price || entry.net_total || 0)
            : 0;
          return {
            storeId,
            itemId,
            name,
            net,
            storeBankId: isObj
              ? entry.store_bank_id || entry.storeBankId
              : null,
            externalTransferId: isObj
              ? entry.external_transfer_id || entry.externalTransferId
              : null,
            errorCode: isObj ? entry.error_code || entry.errorCode : null,
            errorMessage: isObj
              ? entry.error_message || entry.errorMessage
              : null,
            bankAccountName: isObj
              ? entry.bank_account_name || entry.bankAccountName
              : null,
            bankAccountNumber: isObj
              ? entry.bank_account_number || entry.bankAccountNumber
              : null,
            bankCode: isObj ? entry.bank_code || entry.bankCode : null,
            apiStatus: isObj ? entry.status || null : null,
          };
        };
        const toDoneIdSet = (arr) => {
          if (!Array.isArray(arr)) return new Set();
          return new Set(
            arr.map((e) => {
              if (e && typeof e === "object") {
                return e.store_id || e.store_source_id || e.source_id || e.id;
              }
              return e;
            })
          );
        };
        const mapTaskStatus = (entry, completedByList) => {
          const s = String((entry && entry.status) || "").toLowerCase();
          if (s === "success" || s === "completed") return "completed";
          if (s === "failed" || s === "error") return "failed";
          if (s === "in_progress") return "in_progress";
          if (s === "cancelled") return "cancelled";
          if (completedByList) return "completed";
          return "pending";
        };
        const mapTasks = (batch) => {
          const entries = Array.isArray(batch.store_list)
            ? batch.store_list
            : [];
          const done = toDoneIdSet(batch.transferred_store_list);
          const tasks = entries.map((e) => {
            const normalized = normalizeEntry(e);
            const completedByList = done.has(normalized.storeId);
            const status = mapTaskStatus(e, completedByList);
            return {
              storeId: normalized.storeId,
              itemId: normalized.itemId,
              storeBankId: normalized.storeBankId,
              storeName: normalized.name,
              amount: normalized.net,
              status,
              progress: status === "completed" ? 100 : 0,
              externalTransferId: normalized.externalTransferId,
              errorCode: normalized.errorCode,
              errorMessage: normalized.errorMessage,
              bankAccountName: normalized.bankAccountName,
              bankAccountNumber: normalized.bankAccountNumber,
              bankCode: normalized.bankCode,
            };
          });
          tasks.sort((a, b) => {
            const aDone = a.status === "completed";
            const bDone = b.status === "completed";
            if (aDone !== bDone) return aDone ? 1 : -1;
            const aa = Number(a.amount || 0);
            const bb = Number(b.amount || 0);
            if (aa !== bb) return aa - bb;
            return String(a.storeName || "").localeCompare(
              String(b.storeName || "")
            );
          });
          return tasks;
        };
        this.transferRecords = (rows || []).map((b) => {
          const tasks = mapTasks(b);
          const completed = tasks.filter(
            (t) => t.status === "completed"
          ).length;
          const progress = tasks.length
            ? Math.round((completed / tasks.length) * 100)
            : 0;
          const statusFromApi = b.record_status || null;
          return {
            id: b.id,
            transferDate: b.transfer_start_date
              ? moment(b.transfer_start_date).local().format("YYYY-MM-DD HH:mm")
              : "",
            tasks,
            totalAmount: tasks.reduce((s, t) => s + Number(t.amount || 0), 0),
            // Location fields from API for display
            provinceId: b.province_id || null,
            districtId: b.district_id || null,
            provinceName:
              b.province_name_en || b.province_name_th || b.province_name || "",
            districtName:
              b.district_name_en || b.district_name_th || b.district_name || "",
            status: statusFromApi
              ? statusFromApi
              : completed === tasks.length && tasks.length > 0
              ? "completed"
              : completed > 0
              ? "in_progress"
              : "pending",
            progress,
            notes: "",
            pauseReason:
              b.pause_reason ||
              b.batch_pause_reason ||
              b.pauseReason ||
              b.pauseReasonText ||
              null,
          };
        });
      } catch (e) {
        this.notify("Failed to load transfer records", "error");
      } finally {
        if (this._recordsRequestId === requestId) {
          this.recordsLoading = false;
        }
      }
    },
    // Debounced save for config changes
    queueSaveConfig() {
      // Do not auto-save while hydrating/patching or if no district selected
      if (
        this.isHydratingConfig ||
        this.transferConfigLoading ||
        !this.configDistrict ||
        Date.now() < this.hydratingUntil
      )
        return;
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        this.saveConfig();
      }, 600);
    },

    buildTrend() {
      // Aggregate net per day across stores from incomeProfits (Store Income dataset)
      const byDay = {};
      this.incomeProfits.forEach((p) => {
        const key = p.date;
        const net = this.netPrice(p);
        byDay[key] = (byDay[key] || 0) + net;
      });
      const points = Object.keys(byDay)
        .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
        .map((d) => [moment(d).valueOf(), Math.round(byDay[d] * 100) / 100]);
      this.trendSeries = [{ name: "Net", data: points }];
    },

    sparklineFor(item) {
      const series = this.profits
        .filter((p) => p.id === item.id)
        .sort((a, b) => moment(a.date) - moment(b.date))
        .slice(-14)
        .map((p) => Math.round(this.netPrice(p)));
      return [{ name: "net", data: series }];
    },

    // Config
    async saveConfig() {
      try {
        const key = this.locationKey(this.configProvince, this.configDistrict);
        if (!this.configProvince || !this.configDistrict) {
          this.notify(
            "Please select province and district for config",
            "warning"
          );
          return;
        }
        // If enabling auto record, enforce schedule prerequisites
        if (this.config.autoRecord && !this.validateRecordPrereqs()) {
          this.notify(
            "Please set a valid schedule time and, for monthly, a schedule day",
            "warning"
          );
          return;
        }
        // Build current API-shaped payload from UI
        const currentApi = {
          district_id: this.configDistrict,
          province_id: this.configProvince,
          is_auto_transfer_enabled: !!this.config.autoTransfer,
          transfer_record_enabled: !!this.config.autoRecord,
          auto_transfer_type: this.config.frequency,
          schedule_time: this.toUtcTimeStr(this.config.scheduleTime),
          schedule_date:
            this.config.frequency === "monthly" && this.config.scheduleDate
              ? Number(this.config.scheduleDate)
              : null,
        };
        // Compute diff vs last server config; always include district_id
        const base = this.lastServerConfig || {};
        const diff = { district_id: currentApi.district_id };
        const keys = [
          "province_id",
          "is_auto_transfer_enabled",
          "transfer_record_enabled",
          "auto_transfer_type",
          "schedule_time",
          "schedule_date",
        ];
        keys.forEach((k) => {
          const prev = base[k];
          const cur = currentApi[k];
          // Special case: when switching to daily, ensure schedule_date clears
          if (
            k === "schedule_date" &&
            currentApi.auto_transfer_type === "daily"
          ) {
            if (prev !== null) diff[k] = null;
            return;
          }
          if (prev !== cur) diff[k] = cur;
        });
        // If no changes besides district_id, skip PATCH
        const changedKeys = Object.keys(diff).filter(
          (k) => k !== "district_id"
        );
        if (changedKeys.length === 0) return;
        const res = await this.patchTransferConfig(diff);
        if (res === false) throw new Error("Patch failed");
        // Keep a local map cache for scheduler and UX continuity
        this.configMap[key] = {
          autoRecord: currentApi.transfer_record_enabled,
          autoTransfer: currentApi.is_auto_transfer_enabled,
          scheduleDate: this.config.scheduleDate,
          scheduleTime: this.config.scheduleTime,
          frequency: this.config.frequency,
        };
        // Merge changes into lastServerConfig baseline
        this.lastServerConfig = Object.assign({}, base, diff);
        localStorage.setItem(
          "flashpay-transfer-config-map",
          JSON.stringify(this.configMap)
        );
        this.notify("Configuration saved", "success");
      } catch (e) {
        this.notify("Failed to save configuration", "error");
      }
    },
    restoreConfig() {
      try {
        const rawMap = localStorage.getItem("flashpay-transfer-config-map");
        if (rawMap) this.configMap = JSON.parse(rawMap) || {};
        else {
          const raw = localStorage.getItem("flashpay-transfer-config");
          if (raw) {
            const cfg = JSON.parse(raw);
            this.config = Object.assign({}, this.config, cfg);
          }
        }
        // Location defaults will be initialized after provinces load in created()
      } catch (e) {
        /* ignore */
      }
    },

    // Auto scheduler helpers
    loadLastAutoRunMap() {
      try {
        const raw = localStorage.getItem("flashpay-auto-run-log");
        this.lastAutoRunMap = raw ? JSON.parse(raw) : {};
      } catch (e) {
        this.lastAutoRunMap = {};
      }
    },
    saveLastAutoRunMap() {
      try {
        localStorage.setItem(
          "flashpay-auto-run-log",
          JSON.stringify(this.lastAutoRunMap)
        );
      } catch (e) {}
    },
    ensureAutoRunner() {
      if (this.autoRunnerTimer) return;
      this.autoRunnerTimer = setInterval(this.tickAutoRunner, 30000);
    },
    tickAutoRunner() {
      const key = this.locationKey(this.configProvince, this.configDistrict);
      const cfg = this.configMap[key];
      if (!cfg || (!cfg.autoRecord && !cfg.autoTransfer)) return;
      const now = moment();
      const isTimeMatch = now.format("HH:mm") === (cfg.scheduleTime || "09:00");
      if (cfg.frequency === "daily") {
        const last =
          this.lastAutoRunMap[key] && this.lastAutoRunMap[key].lastDaily;
        const todayStr = now.format("YYYY-MM-DD");
        if (isTimeMatch && last !== todayStr) {
          this.performAutoTransfer(cfg, key, "daily", !!cfg.autoTransfer);
        }
      } else if (cfg.frequency === "monthly") {
        let runDayNum;
        if (typeof cfg.scheduleDate === "number") {
          runDayNum = cfg.scheduleDate;
        } else {
          const runDay = cfg.scheduleDate || now.format("YYYY-MM-DD");
          runDayNum = moment(runDay).date();
        }
        const isDayMatch = now.date() === runDayNum;
        const last =
          this.lastAutoRunMap[key] && this.lastAutoRunMap[key].lastMonthly;
        const monthStr = now.format("YYYY-MM");
        if (isDayMatch && isTimeMatch && last !== monthStr) {
          this.performAutoTransfer(cfg, key, "monthly", !!cfg.autoTransfer);
        }
      }
    },
    performAutoTransfer(cfg, key, mode, startImmediately = false) {
      const eligible = this.filteredProfits;
      if (eligible.length === 0) return;
      const ids = eligible.map((p) => p.id);
      const amounts = {};
      eligible.forEach((p) => {
        amounts[p.id] = this.netPrice(p);
      });
      const record = this.buildRecord({
        storeIds: ids,
        amounts,
        transferDate: moment().format("YYYY-MM-DD HH:mm"),
        notes:
          mode === "daily"
            ? `Auto-scheduled ${
                startImmediately ? "transfer" : "record"
              } (daily)`
            : `Auto-scheduled ${
                startImmediately ? "transfer" : "record"
              } (monthly)`,
      });
      this.transferRecords.unshift(record);
      if (startImmediately) {
        this.startTransfer(record);
      }
      if (!this.lastAutoRunMap[key]) this.lastAutoRunMap[key] = {};
      if (mode === "daily")
        this.lastAutoRunMap[key].lastDaily = moment().format("YYYY-MM-DD");
      else this.lastAutoRunMap[key].lastMonthly = moment().format("YYYY-MM");
      this.saveLastAutoRunMap();
    },

    // Selection -> create and start transfer immediately
    startTransferFromSelection() {
      if (this.selectedStores.length === 0) return;
      const ids = this.selectedStores.map((s) => s.id);
      const amounts = {};
      ids.forEach((id) => {
        const store = this.filteredProfits.find((p) => p.id === id);
        const net = store ? this.netPrice(store) : 0;
        amounts[id] = net;
      });
      const record = this.buildRecord({
        storeIds: ids,
        amounts,
        transferDate: moment().format("YYYY-MM-DD HH:mm"),
        notes: "Auto-created from selection",
      });
      this.transferRecords.unshift(record);
      this.selectedStores = [];
      this.startTransfer(record);
    },

    // Records CRUD helpers
    buildRecord({ storeIds, amounts, transferDate, notes }) {
      const id = this.transferRecords.length
        ? Math.max(...this.transferRecords.map((r) => r.id)) + 1
        : 1;
      const tasks = storeIds.map((id) => {
        const p = this.profits.find((x) => x.id === id);
        return {
          storeId: id,
          storeName: p ? p.storeName : `Store ${id}`,
          amount: Math.max(0, Number(amounts[id] || 0)),
          status: "pending",
          progress: 0,
        };
      });
      const totalAmount = tasks.reduce((sum, t) => sum + t.amount, 0);
      return {
        id,
        transferDate,
        tasks,
        totalAmount,
        status: "pending",
        progress: 0,
        notes,
      };
    },

    startTransfer(record) {
      if (this.activeTransfer) {
        this.notify("Another transfer is already in progress", "warning");
        return;
      }
      if (!record || !record.tasks || record.tasks.length === 0) {
        this.notify("No stores to transfer", "error");
        return;
      }
      record.status = "in_progress";
      record.tasks.forEach((t) => {
        if (t.status === "pending" || t.status === "paused")
          t.status = "in_progress";
      });
      this.activeTransferId = record.id;
      this.ensureProgressLoop();
      this.notify(`Transfer #${record.id} started`, "success");
    },

    async setTransferBatchStatus(record, recordStatus, message, notifyType) {
      if (!record || !record.id) return;
      if (this.updatingBatchStatus[record.id]) return;
      this.$set(this.updatingBatchStatus, record.id, true);
      try {
        const updated = await this.updateTransferBatchStatus({
          batch_id: record.id,
          record_status: recordStatus,
        });
        if (!updated) throw new Error("Update failed");

        // Stop any local progress loop if we're pausing/cancelling
        if (
          recordStatus !== "in_progress" &&
          this.activeTransferId === record.id
        ) {
          this.activeTransferId = null;
          this.clearProgressLoopIfIdle();
        }
        await this.fetchTransferRecords();
        this.notify(message, notifyType);
      } catch (e) {
        this.notify("Failed to update transfer status", "error");
      } finally {
        this.$delete(this.updatingBatchStatus, record.id);
      }
    },

    pauseTransfer(record) {
      if (!record || record.status !== "in_progress") return;
      return this.setTransferBatchStatus(
        record,
        "paused",
        `Transfer #${record.id} paused`,
        "warning"
      );
    },

    resumeTransfer(record) {
      if (!record || record.status !== "paused") return;
      return this.setTransferBatchStatus(
        record,
        "in_progress",
        `Transfer #${record.id} resumed`,
        "success"
      );
    },

    stopTransfer(record) {
      if (!record) return;
      if (record.status === "completed" || record.status === "cancelled")
        return;
      return this.setTransferBatchStatus(
        record,
        "cancelled",
        `Transfer #${record.id} cancelled`,
        "error"
      );
    },

    // Progress loop
    ensureProgressLoop() {
      if (this.progressTimer) return;
      this.progressTimer = setInterval(this.tickProgress, 600);
    },
    clearProgressLoopIfIdle() {
      if (this.activeTransfer) return;
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    tickProgress() {
      const rec = this.activeTransfer;
      if (!rec) return;
      let anyInProgress = false;
      rec.tasks.forEach((t) => {
        if (t.status === "in_progress") {
          anyInProgress = true;
          const step = 5 + Math.floor(Math.random() * 12);
          t.progress = Math.min(100, t.progress + step);
          if (t.progress >= 100) {
            t.status = "completed";
            t.progress = 100;
          }
        }
      });
      rec.progress = Math.round(
        rec.tasks.reduce((s, t) => s + t.progress, 0) / rec.tasks.length
      );
      if (rec.tasks.every((t) => t.status === "completed")) {
        rec.status = "completed";
        this.activeTransferId = null;
        this.clearProgressLoopIfIdle();
        this.notify(`Transfer #${rec.id} completed`, "success");
      } else if (!anyInProgress) {
        // No task progressing (all paused?)
        rec.status = "paused";
        this.activeTransferId = null;
        this.clearProgressLoopIfIdle();
      }
    },

    // Create dialog
    openCreateDialog() {
      this.createDialog = true;
      this.currentStep = 1;
      // init defaults
      this.form.province = this.filters.selectedProvince || null;
      this.form.district = null;
      this.form.minNetPrice = 0;
      this.form.selectedStoreIds = [];
      this.form.amounts = {};
      this.form.autoCalculate = true;
      this.form.transferDate = moment().format("YYYY-MM-DD");
      this.form.transferTime = moment().format("HH:mm");
      this.form.notes = "";
    },
    // Multi-step navigation
    nextStep() {
      if (this.currentStep < 3 && this.canProceedToNextStep) {
        this.currentStep++;
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    goToStep(step) {
      if (
        step < this.currentStep ||
        (step === this.currentStep + 1 && this.canProceedToNextStep)
      ) {
        this.currentStep = step;
      }
    },
    getStepDescription() {
      const descriptions = {
        1: "Step 1: Select location and set filters",
        2: "Step 2: Choose stores for transfer",
        3: "Step 3: Review and confirm transfer",
      };
      return (
        descriptions[this.currentStep] ||
        "Configure and initiate a new transfer"
      );
    },
    // Store selection helpers
    isStoreSelected(storeId) {
      return this.form.selectedStoreIds.includes(storeId);
    },
    toggleStoreSelection(storeId) {
      const idx = this.form.selectedStoreIds.indexOf(storeId);
      if (idx > -1) {
        this.form.selectedStoreIds.splice(idx, 1);
      } else {
        this.form.selectedStoreIds.push(storeId);
      }
    },
    selectAllStores() {
      this.form.selectedStoreIds = this.eligibleStores.map((s) => s.id);
    },
    deselectAllStores() {
      this.form.selectedStoreIds = [];
    },
    getRecordLocation(record) {
      // Prefer explicit location from batch response
      if (record && (record.provinceName || record.districtName)) {
        const p = record.provinceName || record.provinceId || "";
        const d = record.districtName || record.districtId || "";
        if (p || d) return `${p || "?"} > ${d || "?"}`;
      }
      // Fallback: try infer from first task's store (legacy approach)
      if (record.tasks && record.tasks.length > 0) {
        const storeId = record.tasks[0].storeId;
        const store = this.profits.find((p) => p.id === storeId);
        if (store && store.province && store.district) {
          return `${store.province} > ${store.district}`;
        }
      }
      return "Location N/A";
    },

    openStoreListModal(record) {
      this.storeListModal.record = record;
      this.storeListModal.search = "";
      this.storeListModal.show = true;
    },

    closeTransferItemModal() {
      this.transferItemModal.show = false;
      this.transferItemModal.loading = false;
      this.transferItemModal.error = null;
      this.transferItemModal.item = null;
      this.transferItemModal.task = null;
      this.transferItemModal.record = null;
    },

    async openTransferItemModal({ task, record }) {
      this.transferItemModal.show = true;
      this.transferItemModal.loading = true;
      this.transferItemModal.error = null;
      this.transferItemModal.item = null;
      this.transferItemModal.task = task || null;
      this.transferItemModal.record = record || null;

      const itemId = task && task.itemId;
      if (!itemId) {
        this.transferItemModal.loading = false;
        this.transferItemModal.error =
          "This store row has no item_id from ListBatches, so details cannot be fetched.";
        return;
      }

      try {
        const item = await this.fetchTransferItemById({ item_id: itemId });
        if (!item) throw new Error("not_found");
        this.transferItemModal.item = item;
      } catch (e) {
        this.transferItemModal.error =
          "Failed to load transfer-item details (item not found or API error).";
      } finally {
        this.transferItemModal.loading = false;
      }
    },

    storeNameById(id) {
      const p = this.profits.find((x) => x.id === id);
      return p ? p.storeName : `Store ${id}`;
    },
    submitCreateRecord() {
      if (!this.$refs.createForm) return;
      const ok = this.$refs.createForm.validate();
      if (!ok) return;
      if (this.form.selectedStoreIds.length === 0) return;
      // Prepare amounts
      const amounts = {};
      this.form.selectedStoreIds.forEach((id) => {
        if (this.form.autoCalculate) {
          const p = this.profits.find((x) => x.id === id);
          amounts[id] = p ? this.netPrice(p) : 0;
        } else {
          amounts[id] = Math.max(0, Number(this.form.amounts[id] || 0));
        }
      });
      const transferDate = `${this.formatDate(this.form.transferDate)} ${
        this.form.transferTime
      }`;
      const record = this.buildRecord({
        storeIds: this.form.selectedStoreIds.slice(),
        amounts,
        transferDate,
        notes: this.form.notes,
      });
      this.transferRecords.unshift(record);
      this.createDialog = false;
      this.notify(`Transfer #${record.id} created`, "success");
    },

    computeDashboard() {
      // Total profit = sum of net price for today across Store Income dataset
      const today = moment().format("YYYY-MM-DD");
      const totalProfit = this.incomeProfits
        .filter((p) => p.date === today)
        .reduce((s, p) => s + this.netPrice(p), 0);
      const pendingAmount = this.transferRecords
        .filter((r) => r.status === "pending" || r.status === "paused")
        .reduce((s, r) => s + (r.totalAmount || 0), 0);
      const completedToday = this.transferRecords
        .filter(
          (r) =>
            r.status === "completed" &&
            r.transferDate &&
            moment(r.transferDate).isSame(moment(), "day")
        )
        .reduce((s, r) => s + (r.totalAmount || 0), 0);

      this.stats.totalProfitDisplay = this.asCurrency(totalProfit);
      this.stats.pendingAmountDisplay = this.asCurrency(pendingAmount);
      this.stats.completedTodayDisplay = this.asCurrency(completedToday);
    },

    // Notifications
    notify(msg, color = "success") {
      this.snackbar.message = msg;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    profitHeaderHint(value) {
      const map = {
        storeName: "Store identity and order count",
        orderCodes: "Open the order-level profit breakdown",
        netPriceDisplay: "Visible net amount for the grouped store rows",
        cutoffSummary: "Current cutoff completion for this store",
        paidSummary: "Current payout completion for this store",
        profitActions: "Manual controls for grouped profit rows",
        expand: "Reveal supporting store metrics",
      };
      return map[value] || "Store profit table column";
    },
    orderHeaderHint(value) {
      const map = {
        selected: "Select editable order rows for bulk actions",
        orderCode: "Order code and profit row id",
        orderDateTime: "Source order timestamp",
        netPrice: "Net profit for this order row",
        isCutoff: "Cutoff state for this row",
        isPaid: "Payout state for this row",
        guard: "Whether manual editing is allowed and which flow applies",
        actions: "Only the next valid step and direct undo are shown",
        expand: "Reveal additional row metrics",
      };
      return map[value] || "Order profit table column";
    },
    storeInitials(name) {
      const words = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!words.length) return "ST";
      return words
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
    },
  },
};
</script>

<style lang="scss" scoped>
.flashpay-transfer-page {
  .v-toolbar__title {
    font-weight: 600;
  }
}

.code-block {
  margin: 0;
  padding: 12px;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 240px;
  overflow: auto;
}

.fab-btn {
  bottom: 24px;
  right: 24px;
}

.create-btn {
  bottom: 100px;
  right: 50px;
  border-radius: 999px;
  animation: fabPulse 2s infinite;
}

@keyframes fabPulse {
  0%,
  100% {
    box-shadow: 0 8px 24px rgba(124, 58, 237, 0.5);
  }
  50% {
    box-shadow: 0 12px 32px rgba(124, 58, 237, 0.8);
  }
}

/* Create Modal Styling */
.create-modal {
  background: rgba(15, 23, 42, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  margin: 0 !important;
}

.modal-header {
  padding: 24px 32px !important;
  background: linear-gradient(
    135deg,
    rgba(124, 58, 237, 0.1),
    rgba(34, 211, 238, 0.1)
  );
}

.modal-title {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.gradient-text {
  background: linear-gradient(135deg, #7c3aed, #22d3ee);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.modal-subtitle {
  color: #94a3b8;
  font-size: 14px;
  margin-top: 4px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.05) !important;
  transition: all 0.3s;
  color: white;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: rotate(90deg);
}

.modal-divider {
  background: rgba(255, 255, 255, 0.1) !important;
}

.modal-body {
  padding: 32px !important;
  max-height: 50vh;
  overflow-y: scroll;
}

.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(124, 58, 237, 0.5);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(124, 58, 237, 0.7);
}

.glass-input ::v-deep .v-input__control {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.glass-input ::v-deep .v-input__slot {
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}

.glass-input ::v-deep .v-input__slot:hover {
  border-color: rgba(124, 58, 237, 0.5);
  background: rgba(255, 255, 255, 0.08);
}

.auto-calc-switch {
  margin-top: 12px;
}

.switch-label {
  color: #e5e7eb;
  font-size: 14px;
}

.amounts-preview {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.preview-label {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-actions {
  padding: 20px 32px 24px !important;
  background: linear-gradient(to top, rgba(15, 23, 42, 0.8), transparent);
}

.cancel-btn {
  color: #94a3b8 !important;
  transition: all 0.3s;
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.05) !important;
  color: #e5e7eb !important;
}

.submit-btn {
  padding: 0 32px !important;
  border-radius: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(124, 58, 237, 0.4);
}

.avatar.small {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  font-size: 12px;
}

.headline {
  font-weight: 600;
}

.subtitle-2 {
  font-weight: 500;
}
/* Modern dark surface and glassmorphism */
.dark-surface {
  background: radial-gradient(
      1200px 800px at 80% -200px,
      rgba(124, 58, 237, 0.08),
      transparent
    ),
    radial-gradient(
      800px 600px at -100px 200px,
      rgba(34, 211, 238, 0.08),
      transparent
    ),
    #0f172a;
  min-height: 100vh;
}

.glass-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
  margin: 10px;
}

/* Profit table modern styling */
.overview-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.overview-kicker,
.order-profit-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #7dd3fc;
}

.overview-subtitle,
.order-profit-subtitle {
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
  max-width: 540px;
}

.overview-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.overview-stat-card,
.order-profit-summary-card {
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.09),
      rgba(255, 255, 255, 0.03)
    ),
    rgba(15, 23, 42, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.overview-stat-label,
.summary-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #94a3b8;
}

.overview-stat-value,
.summary-value {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 700;
  color: #f8fafc;
}

.overview-summary-band {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.overview-summary-pill,
.order-profit-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
}

.overview-summary-pill .label,
.order-profit-stat-pill .label {
  color: #94a3b8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.overview-summary-pill .value,
.order-profit-stat-pill .value {
  font-weight: 700;
  color: #f8fafc;
}

.table-header-label {
  display: inline-flex;
  align-items: center;
  color: #94a3b8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
}

.order-profit-stat-pill.danger {
  border-color: rgba(245, 158, 11, 0.35);
  background: rgba(120, 53, 15, 0.22);
}

.profit-overview-table ::v-deep table,
.order-profit-table ::v-deep table {
  border-collapse: separate;
  border-spacing: 0 12px;
}

.profit-overview-table ::v-deep thead th,
.order-profit-table ::v-deep thead th {
  border-bottom: none !important;
  color: #94a3b8 !important;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
}

.profit-overview-table ::v-deep tbody tr,
.order-profit-table ::v-deep tbody tr {
  background: #ffffff;
}

.profit-overview-table ::v-deep tbody td,
.order-profit-table ::v-deep tbody td {
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  background: #ffffff;
  padding-top: 14px !important;
  padding-bottom: 14px !important;
  color: #0f172a;
}

.profit-overview-table ::v-deep tbody td:first-child,
.order-profit-table ::v-deep tbody td:first-child {
  border-left: 1px solid rgba(148, 163, 184, 0.18);
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
}

.profit-overview-table ::v-deep tbody td:last-child,
.order-profit-table ::v-deep tbody td:last-child {
  border-right: 1px solid rgba(148, 163, 184, 0.18);
  border-top-right-radius: 18px;
  border-bottom-right-radius: 18px;
}

.profit-overview-table ::v-deep tbody tr.v-datatable__expand-content td,
.order-profit-table ::v-deep tbody tr.v-datatable__expand-content td {
  background: transparent;
  border: none;
  padding-top: 0 !important;
}

.modern-table .row-hover {
  padding: 10px;
}
.modern-table .row-main .store-name {
  font-weight: 700;
  color: #0f172a;
}
.modern-table .row-main .store-sub {
  color: #64748b;
  font-size: 12px;
}

.overview-store-cell {
  display: flex;
  align-items: center;
  gap: 14px;
}

.overview-store-avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f766e, #0891b2);
  color: #ecfeff;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  box-shadow: 0 10px 24px rgba(8, 145, 178, 0.22);
}

.overview-micro-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.overview-mini-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
  font-size: 11px;
  font-weight: 600;
}

.overview-mini-tag.locked {
  color: #fcd34d;
  border-color: rgba(245, 158, 11, 0.3);
  background: rgba(120, 53, 15, 0.2);
}

.overview-location-card,
.overview-orders-card,
.status-card,
.time-card,
.order-lock-card,
.order-profit-time-card {
  border-radius: 14px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.overview-location-label,
.time-label,
.money-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #94a3b8;
  margin-bottom: 6px;
}

.overview-location-name,
.time-value,
.money-value {
  color: #0f172a;
  font-weight: 600;
}

.overview-orders-card {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  min-width: 112px;
}

.overview-orders-count {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
  margin-bottom: 8px;
}

.overview-link-btn {
  border-radius: 12px;
}

.overview-action-btn {
  min-width: 148px;
}

.table-expand-btn {
  color: #cbd5e1;
  background: rgba(255, 255, 255, 0.05);
}

.table-expand-cell {
  padding: 0 0 12px !important;
}

.table-expand-panel {
  margin: 0 6px;
  padding: 14px;
  border-radius: 18px;
  background: linear-gradient(
      135deg,
      rgba(15, 118, 110, 0.12),
      rgba(14, 165, 233, 0.06)
    ),
    rgba(15, 23, 42, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.table-expand-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.order-expand-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.expand-metric-card {
  border-radius: 14px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.expand-metric-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #94a3b8;
  margin-bottom: 6px;
}

.expand-metric-value {
  color: #f8fafc;
  font-weight: 600;
  line-height: 1.45;
}

.table-page-text {
  color: #94a3b8;
  font-size: 12px;
}

.overview-money-stack {
  display: inline-flex;
  min-width: 120px;
  flex-direction: column;
  align-items: flex-end;
  border-radius: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.overview-money-stack.highlight {
  background: linear-gradient(
      135deg,
      rgba(8, 145, 178, 0.16),
      rgba(14, 165, 233, 0.08)
    ),
    rgba(255, 255, 255, 0.04);
}

.overview-money-stack.compact {
  min-width: 96px;
  padding: 8px 10px;
}

.badge-gradient {
  background: linear-gradient(90deg, #f59e0b, #f97316);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 700;
}
.row-chart {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.row-chart .net {
  color: #22d3ee;
  font-weight: 600;
}
.row-chart .spark {
  width: 80px;
}

.status-card {
  min-width: 150px;
}

.status-card.compact {
  min-width: 120px;
  padding: 10px 12px;
}

.overview-action-stack {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  max-width: 180px;
  margin: 0 auto;
}

.overview-action-stack .v-btn {
  border-radius: 12px;
}

.overview-action-hint {
  margin-top: 8px;
  line-height: 1.4;
}

.order-profit-dialog {
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.98),
    rgba(15, 23, 42, 0.94)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 22px !important;
  overflow: hidden;
}

.order-profit-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px !important;
  background: radial-gradient(
      600px 260px at top right,
      rgba(14, 165, 233, 0.14),
      transparent
    ),
    linear-gradient(135deg, rgba(15, 118, 110, 0.18), rgba(14, 165, 233, 0.08));
}

.order-profit-store {
  margin-top: 12px;
  color: #f8fafc;
  font-size: 15px;
  font-weight: 700;
}

.order-profit-store span {
  color: #94a3b8;
  font-weight: 500;
  margin-left: 6px;
}

.order-profit-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.order-profit-close {
  margin-left: 6px;
  color: #e5e7eb;
}

.order-profit-body {
  padding: 24px 28px 18px !important;
}

.order-profit-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.order-profit-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.order-profit-filters {
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(180px, 220px);
  gap: 12px;
  flex: 1;
}

.order-profit-search ::v-deep .v-input__slot,
.order-profit-filter ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.order-profit-quick-actions {
  display: flex;
  align-items: center;
}

.order-profit-bulk-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.order-profit-bulk-grid .v-btn {
  border-radius: 12px;
}

.order-profit-warning {
  background: rgba(120, 53, 15, 0.16) !important;
}

.order-profit-row-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-profit-code {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.lock-title {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.lock-copy {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}

.order-lock-card.locked {
  border-color: rgba(245, 158, 11, 0.28);
  background: rgba(120, 53, 15, 0.16);
}

.order-profit-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-profit-footer {
  padding: 0 28px 20px !important;
}

/* Control panel */
.control-panel {
  position: relative;
}
.config-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
      135deg,
      rgba(124, 58, 237, 0.12),
      rgba(34, 211, 238, 0.12)
    ),
    rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 16px;
  z-index: 2;
}
.config-loader-overlay .loader-content {
  text-align: center;
  color: #e5e7eb;
}
.config-loader-overlay .loader-text {
  margin-top: 8px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.config-loader-overlay .loader-bars {
  margin-top: 12px;
}
.config-loader-overlay .loader-bars .bar {
  height: 10px;
  width: 220px;
  border-radius: 8px;
  margin: 6px auto;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.08) 25%,
    rgba(255, 255, 255, 0.18) 50%,
    rgba(255, 255, 255, 0.08) 75%
  );
  background-size: 400% 100%;
}
.section-frame {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px;
  margin: 12px 8px;
  background: rgba(255, 255, 255, 0.04);
  position: relative;
}
.section-title {
  font-weight: 600;
  color: #e5e7eb;
  margin-bottom: 8px;
}
.status-led {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #64748b;
  position: absolute;
  right: 16px;
  top: 16px;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  transition: all 0.3s;
}
.status-led.on {
  background: #10b981;
  box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.1);
}

.big-switch ::v-deep .v-input__slot {
  padding-top: 10px;
}
.seg-control ::v-deep .v-btn {
  color: #cbd5e1;
}
.gradient-btn {
  background: linear-gradient(90deg, #7c3aed, #22d3ee);
}

/* Progress cards */
.task-card {
  padding: 16px;
}
.task-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.task-header .avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}
.task-title {
  color: #e5e7eb;
  font-weight: 600;
  flex: 1;
}
.task-body {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.task-body .amount {
  color: #a5b4fc;
  font-weight: 600;
}
.overall-progress ::v-deep .v-progress-linear__background {
  opacity: 0.2;
}

.store-status-list {
  max-height: 360px;
  overflow-y: auto;
}
.store-row {
  display: flex;
  align-items: center;
  padding: 6px 8px;
}
.store-row .name {
  color: #e5e7eb;
  margin-left: 8px;
}
.list-controls {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

/* Timeline */
.timeline-card {
  padding: 8px 0;
}
.timeline {
  position: relative;
}
.timeline-group {
  margin-bottom: 16px;
}
.timeline-date {
  color: #94a3b8;
  font-size: 12px;
  margin: 8px 0 8px 20px;
}
.timeline-items {
  position: relative;
}
.timeline-item {
  position: relative;
  padding-left: 40px;
  margin: 12px 0;
}
.timeline-item .line {
  position: absolute;
  left: 23px;
  top: 0;
  bottom: -12px;
  width: 2px;
  background: rgba(255, 255, 255, 0.08);
}
.timeline-item .dot {
  position: absolute;
  left: 16px;
  top: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #64748b;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.06);
}
.timeline-item .dot.pending {
  background: #94a3b8;
}
.timeline-item .dot.in_progress {
  background: #0ea5e9;
}
.timeline-item .dot.paused {
  background: #f59e0b;
}
.timeline-item .dot.completed {
  background: #10b981;
}
.timeline-item .dot.failed {
  background: #ef4444;
}
.timeline-item .dot.cancelled {
  background: #ef4444;
}
.timeline-item .content {
  padding: 12px;
}
.timeline-item .row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.timeline-item .header {
  justify-content: space-between;
  margin-bottom: 6px;
}
.timeline-item .title {
  color: #e5e7eb;
  font-weight: 600;
}
.timeline-item .meta {
  color: #94a3b8;
  font-size: 13px;
}
.timeline-item .meta .amount {
  color: #a5b4fc;
  font-weight: 600;
}
.timeline-item .actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.store-chips {
  margin-top: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.pill-search ::v-deep input {
  border-radius: 999px;
}

.headline {
  font-weight: 600;
  color: #e5e7eb;
}
.subtitle-2 {
  font-weight: 500;
}

/* Progress Ring Styles */
.progress-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.progress-ring-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
}

.progress-ring {
  transform: rotate(-90deg);
}

.progress-ring-circle {
  transition: stroke-dashoffset 0.6s ease;
  stroke-linecap: round;
}

.progress-ring-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progress-percentage {
  font-size: 42px;
  font-weight: 700;
  color: #e5e7eb;
  line-height: 1;
}

.progress-subtitle {
  font-size: 14px;
  color: #94a3b8;
  margin-top: 8px;
}

/* Statistics Bar */
.stats-bar {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  font-size: 13px;
  color: #cbd5e1;
}

.stat-item.success {
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.stat-item.in-progress {
  border: 1px solid rgba(34, 211, 238, 0.3);
}

.stat-item.failed {
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.stat-item.pending {
  border: 1px solid rgba(148, 163, 184, 0.3);
}

.pulse-icon {
  animation: pulse-rotate 2s infinite;
}

@keyframes pulse-rotate {
  0%,
  100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(180deg);
  }
}

/* Step Indicator */
.step-indicator {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 32px;
  gap: 60px;
}

.step-line {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 0;
}

.step-dot {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 1;
}

.step-dot.active {
  background: linear-gradient(135deg, #7c3aed, #22d3ee);
  border-color: transparent;
  color: white;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
}

.step-dot.completed {
  background: rgba(16, 185, 129, 0.2);
  border-color: #10b981;
  color: #10b981;
}

.step-dot:hover {
  transform: scale(1.1);
}

/* Step Content */
.step-content {
  animation: stepFadeIn 0.4s ease;
}

@keyframes stepFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.step-title {
  font-size: 20px;
  font-weight: 600;
  color: #e5e7eb;
  margin-bottom: 8px;
}

.step-subtitle {
  font-size: 14px;
  color: #94a3b8;
  margin-bottom: 24px;
}

.helper-text {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
  font-style: italic;
}

@media (max-width: 1264px) {
  .overview-stat-grid,
  .order-profit-summary,
  .order-profit-bulk-grid,
  .table-expand-grid,
  .order-expand-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .order-profit-hero,
  .order-profit-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .order-profit-hero-actions {
    justify-content: flex-start;
  }

  .order-profit-filters {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .overview-stat-grid,
  .order-profit-summary,
  .order-profit-bulk-grid,
  .table-expand-grid,
  .order-expand-grid {
    grid-template-columns: 1fr;
  }

  .overview-store-cell {
    align-items: flex-start;
  }

  .overview-summary-band {
    flex-direction: column;
  }

  .order-profit-body,
  .order-profit-hero,
  .order-profit-footer {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}

/* Store Grid */
.summary-card {
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-around;
  gap: 16px;
}

.summary-item {
  text-align: center;
}

.summary-label {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.summary-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #e5e7eb;
}

.bulk-actions {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 8px;
}

.selection-count {
  color: #22d3ee;
  font-weight: 600;
}

.store-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.store-grid::-webkit-scrollbar {
  width: 8px;
}

.store-grid::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.store-grid::-webkit-scrollbar-thumb {
  background: rgba(124, 58, 237, 0.5);
  border-radius: 4px;
}

.store-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.store-card:hover {
  border-color: rgba(124, 58, 237, 0.5);
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-2px);
}

.store-card.selected {
  border-color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}

.store-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
}

.store-info {
  flex: 1;
}

.store-name {
  font-weight: 600;
  color: #e5e7eb;
  font-size: 14px;
}

.store-price {
  font-size: 16px;
  font-weight: 700;
  color: #22d3ee;
  margin-top: 4px;
}

.commission-badge {
  background: rgba(124, 58, 237, 0.2) !important;
  color: #a78bfa !important;
  font-weight: 600;
}

/* Confirmation Summary */
.confirmation-summary {
  padding: 24px;
  margin-bottom: 20px;
}

.summary-header {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.highlight {
  background: rgba(124, 58, 237, 0.1);
  padding: 16px;
  border-radius: 8px;
  margin: 8px 0;
  border: none;
}

.summary-row .label {
  color: #94a3b8;
  font-size: 14px;
}

.summary-row .value {
  color: #e5e7eb;
  font-weight: 600;
  font-size: 14px;
}

.summary-row .value.amount {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #7c3aed, #22d3ee);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* Location Badge in Timeline */
.location-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 12px;
  padding: 4px 10px;
  background: rgba(124, 58, 237, 0.1);
  border-radius: 12px;
  font-size: 11px;
  color: #a78bfa;
  font-weight: 500;
}

.location-badge .v-icon {
  font-size: 14px;
}

/* Store Chip with Amount Badge */
.store-chip-with-badge {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  padding-right: 8px !important;
}

.store-chip-name {
  font-size: 11px;
}

.store-chip-amount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 4px;
  white-space: nowrap;
}

.clickable-chip {
  cursor: pointer;
  transition: transform 120ms ease, filter 120ms ease, box-shadow 120ms ease;
}

.clickable-chip:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.store-chip-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 6px 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}

/* Store List Modal White Background */
.store-list-modal-card {
  background: white !important;
}

.store-list-modal-card .headline,
.store-list-modal-card .v-card__text,
.store-list-modal-card .v-card__actions,
.store-list-modal-card .caption {
  color: #1a202c !important;
}

.store-list-modal-card .store-name {
  color: #1a202c !important;
}

/* Search Field Styling */
.store-search-field .v-input__slot {
  background: #f7fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
}

.store-search-field input {
  color: #1a202c !important;
}

.store-search-field label {
  color: #64748b !important;
}

.store-search-field .v-icon {
  color: #64748b !important;
}

/* Amount Badge in Modal Table */
.amount-badge {
  display: inline-block;
  padding: 3px 10px;
  background: linear-gradient(135deg, rgb(41, 44, 46), #fddf00);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  color: white;
}

/* Toolbar Enhancement */
.v-toolbar.elevation-0 {
  background: transparent !important;
}

.display-1 {
  background: linear-gradient(135deg, #e5e7eb, #cbd5e1);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 700;
}

/* Responsive Status Filter Buttons */
.v-btn-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
}

.v-btn-toggle .v-btn {
  flex: 1 1 auto;
  min-width: 0;
}

@media (max-width: 599px) {
  /* Mobile: Flexible buttons that share space equally */
  .v-btn-toggle .v-btn {
    font-size: 12px !important;
    padding: 0 8px !important;
    flex: 1 1 0;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  /* Tablet: Slightly smaller buttons */
  .v-btn-toggle .v-btn {
    font-size: 13px !important;
    padding: 0 12px !important;
  }
}

/* Responsive Timeline Cards */
@media (max-width: 599px) {
  /* Mobile: Adjust timeline layout */
  .timeline-item {
    padding-left: 24px;
  }

  .timeline-item .line {
    left: 11px;
  }

  .timeline-item .dot {
    left: 4px;
    width: 14px;
    height: 14px;
  }

  .timeline-item .content {
    padding: 12px 8px;
  }

  .timeline-item .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .timeline-item .title {
    font-size: 13px;
    width: 100%;
  }

  .location-badge {
    display: flex;
    flex-wrap: wrap;
    margin-top: 4px;
    font-size: 11px !important;
  }

  .timeline-item .row.meta {
    flex-wrap: wrap;
    gap: 4px;
    font-size: 12px;
  }

  .timeline-item .row.meta > div {
    flex: 0 0 auto;
  }

  .timeline-item .actions {
    flex-wrap: wrap;
  }

  .timeline-item .actions .v-btn {
    flex: 1 1 auto;
    min-width: 100px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  /* Tablet: Optimize spacing */
  .timeline-item .content {
    padding: 14px 10px;
  }

  .timeline-item .title {
    font-size: 14px;
  }

  .timeline-item .actions .v-btn {
    min-width: 90px;
  }
}
</style>
